<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center ga-3 mb-6">
      <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" @click="goBack">
        Back
      </v-btn>
      <div class="flex-grow-1">
        <div class="text-h5 font-weight-bold d-flex align-center ga-2 flex-wrap">
          <v-icon icon="mdi-vector-flow" size="28" />
          Publish Flow
        </div>
        <div class="text-body-2 text-grey">What happened during this publish run</div>
      </div>
      <v-btn
        variant="text"
        color="primary"
        prepend-icon="mdi-refresh"
        :loading="isLoading"
        :disabled="isLoading"
        @click="loadFlow"
      >
        Refresh
      </v-btn>
    </div>

    <!-- Loading -->
    <v-card v-if="isLoading && !hasAnyData" class="border pa-8 text-center" elevation="0">
      <v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
      <div class="text-h6 mb-2">Loading publish flow…</div>
      <div class="text-body-2 text-grey">
        Reconstructing this publish from job, logs, run metrics, and flow measurements.
      </div>
    </v-card>

    <!-- No accountId -->
    <v-card v-else-if="!accountStore.accountId" class="border pa-8 text-center" elevation="0">
      <v-icon icon="mdi-account-question-outline" size="64" color="grey" class="mb-4" />
      <div class="text-h6 mb-2">No account selected</div>
      <div class="text-body-2 text-grey">Select a dealer account to view a publish run.</div>
    </v-card>

    <!-- Missing correlationId -->
    <v-card v-else-if="!correlationId" class="border pa-8 text-center" elevation="0">
      <v-icon icon="mdi-link-variant-off" size="64" color="grey" class="mb-4" />
      <div class="text-h6 mb-2">No publish run specified</div>
      <div class="text-body-2 text-grey">
        Open a publish flow from <router-link to="/">the dashboard</router-link>
        or pick a run from the publish history.
      </div>
    </v-card>

    <!-- Nothing found at all -->
    <v-card v-else-if="!hasAnyData" class="border pa-8 text-center" elevation="0">
      <v-icon icon="mdi-magnify-close" size="64" color="grey" class="mb-4" />
      <div class="text-h6 mb-2">No publish data found</div>
      <div class="text-body-2 text-grey">
        We couldn't locate any job, logs, run metrics, or flow measurements for this correlation ID.
      </div>
      <v-alert
        v-if="aggregatedErrorMessage"
        type="error"
        variant="tonal"
        density="comfortable"
        class="mt-4 text-start"
      >
        {{ aggregatedErrorMessage }}
      </v-alert>
    </v-card>

    <div v-else>
      <!-- Run Summary -->
      <v-card class="border mb-4" elevation="0">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-store-check-outline" class="mr-2" />
          Run Summary
          <v-spacer />
          <v-chip v-if="runSummary" :color="runSummary.statusColor" size="small" variant="flat">
            {{ runSummary.status }}
          </v-chip>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div v-if="!publishOutcome.hasOutcome" class="text-body-2 text-grey">
            We couldn't load inventory outcome for this publish.
          </div>

          <div v-else>
            <div class="publish-outcome-hero">
              <div class="publish-outcome-count">
                <template v-if="publishOutcome.liveWebsiteCount != null">
                  {{ publishOutcome.liveWebsiteCount }}
                </template>
                <template v-else>—</template>
              </div>
              <div>
                <div class="text-h6 font-weight-bold">vehicles live on website after publish</div>
                <div class="text-body-2 text-grey">
                  Live website outcome after this publish. Only vehicles whose live website state
                  changed are counted below.
                </div>
              </div>
            </div>

            <div class="publish-outcome-grid mt-4">
              <div class="publish-outcome-metric">
                <div class="publish-outcome-metric-value">{{ inventoryChanges.hasMetrics ? publishOutcome.added : '—' }}</div>
                <div class="publish-outcome-metric-label">Added to website</div>
              </div>
              <div class="publish-outcome-metric">
                <div class="publish-outcome-metric-value">{{ inventoryChanges.hasMetrics ? publishOutcome.removed : '—' }}</div>
                <div class="publish-outcome-metric-label">Removed from website</div>
              </div>
              <div class="publish-outcome-metric">
                <div class="publish-outcome-metric-value">{{ inventoryChanges.hasMetrics ? publishOutcome.updated : '—' }}</div>
                <div class="publish-outcome-metric-label">Updated on website</div>
              </div>
              <div class="publish-outcome-metric">
                <div class="publish-outcome-metric-value">{{ inventoryChanges.hasMetrics ? publishOutcome.madeVisible : '—' }}</div>
                <div class="publish-outcome-metric-label">Made visible</div>
              </div>
              <div class="publish-outcome-metric">
                <div class="publish-outcome-metric-value">{{ inventoryChanges.hasMetrics ? publishOutcome.hidden : '—' }}</div>
                <div class="publish-outcome-metric-label">Hidden</div>
              </div>
            </div>
          </div>

          <v-alert
            v-if="runSummary?.failure?.hasFailure"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mt-4"
            data-testid="publish-flow-failure-panel"
          >
            <div class="text-subtitle-2 font-weight-bold mb-1">
              {{ runSummary.failure.title }}
            </div>

            <div class="text-body-2">
              <span class="font-weight-medium">Where:</span>
              {{ runSummary.failure.stageLabel }}
            </div>

            <div class="text-body-2 mt-1">
              <span class="font-weight-medium">Reason:</span>
              {{ runSummary.failure.reason }}
            </div>

            <div
              v-if="
                runSummary.failure.expectedFeeds.length ||
                runSummary.failure.completedFeeds.length ||
                runSummary.failure.failedFeeds.length
              "
              class="text-body-2 mt-2"
            >
              <div v-if="runSummary.failure.expectedFeeds.length">
                <span class="font-weight-medium">Expected feeds:</span>
                {{ runSummary.failure.expectedFeeds.join(', ') }}
              </div>
              <div v-if="runSummary.failure.completedFeeds.length">
                <span class="font-weight-medium">Completed feeds:</span>
                {{ runSummary.failure.completedFeeds.join(', ') }}
              </div>
              <div v-if="runSummary.failure.failedFeeds.length">
                <span class="font-weight-medium">Failed feeds:</span>
                {{ runSummary.failure.failedFeeds.join(', ') }}
              </div>
            </div>

            <div class="text-caption mt-2">Correlation ID: {{ correlationId }}</div>
          </v-alert>

          <v-expansion-panels v-if="runSummary" variant="accordion" class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-title> More info </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="run-summary-grid">
                  <div class="run-summary-cell">
                    <div class="text-caption text-grey">Triggered by</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ formatTriggerLabel(runSummary) }}
                    </div>
                  </div>
                  <div class="run-summary-cell">
                    <div class="text-caption text-grey">Started</div>
                    <div class="text-body-2 font-weight-medium">
                      <DateTimeWithDealerTooltip
                        v-if="runSummary.startedAt"
                        :value="runSummary.startedAt"
                      />
                      <span v-else>—</span>
                    </div>
                  </div>
                  <div class="run-summary-cell">
                    <div class="text-caption text-grey">Finished</div>
                    <div class="text-body-2 font-weight-medium">
                      <DateTimeWithDealerTooltip
                        v-if="runSummary.finishedAt"
                        :value="runSummary.finishedAt"
                      />
                      <span v-else>—</span>
                    </div>
                  </div>
                  <div class="run-summary-cell">
                    <div class="text-caption text-grey">Duration</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ formatDuration(runSummary.durationMs) }}
                    </div>
                  </div>
                  <div class="run-summary-cell">
                    <div class="text-caption text-grey">Feed</div>
                    <div class="text-body-2 font-weight-medium">
                      {{ runSummary.feedName || '—' }}
                    </div>
                  </div>
                  <div class="run-summary-cell run-summary-cell--wide">
                    <div class="text-caption text-grey">Correlation ID</div>
                    <div class="text-body-2 font-weight-medium">
                      <code class="correlation-chip">{{ correlationId || '—' }}</code>
                    </div>
                  </div>
                </div>

                <div
                  v-if="inventoryChanges.hasMetrics"
                  class="mt-5"
                  data-testid="publish-flow-summary-inventory-outcome-details"
                >
                  <div class="text-subtitle-2 font-weight-bold mb-2">Inventory outcome details</div>

                  <div class="summary-inventory-outcome-grid">
                    <ChangeBucketCard
                      title="Feed-applied"
                      subtitle="Caused by the feed file processed in this run"
                      :bucket="inventoryChanges.feed"
                      compact
                    />
                    <ChangeBucketCard
                      title="Manual"
                      subtitle="Caused by user actions promoted in this publish"
                      :bucket="inventoryChanges.manual"
                      compact
                    />
                    <ChangeBucketCard
                      title="Total"
                      subtitle="Combined feed + manual"
                      :bucket="inventoryChanges.totals"
                      compact
                      emphasize
                    />
                  </div>

                  <v-alert
                    v-if="inventoryChanges.truncated"
                    type="info"
                    variant="tonal"
                    density="comfortable"
                    class="mt-3"
                  >
                    The metrics for this run are incomplete. Complete totals are unavailable.
                  </v-alert>
                </div>

                <v-alert
                  v-if="errors.runMetrics"
                  type="warning"
                  variant="tonal"
                  density="comfortable"
                  class="mt-3"
                >
                  Run metrics lookup failed: {{ errors.runMetrics }}
                </v-alert>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-alert
            v-if="errors.job"
            type="warning"
            variant="tonal"
            density="comfortable"
            class="mt-3"
          >
            Job lookup partially failed: {{ errors.job }}
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Canonical Publish Flow stages -->
      <v-card class="border mb-4" elevation="0" data-testid="publish-flow-canonical-stages">
        <v-card-title class="d-flex align-center">
          <v-icon icon="mdi-format-list-checks" class="mr-2" />
          Publish Stages
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div class="publish-stage-flow" data-testid="publish-flow-steps">
          <div
            v-for="(section, sectionIndex) in publishFlowBusinessSections"
            :key="section.id"
            class="publish-stage-flow-row"
            data-testid="publish-flow-step"
            :data-step-index="sectionIndex + 1"
          >
            <div class="publish-stage-flow-rail" aria-hidden="true">
              <div class="publish-stage-flow-marker" data-testid="publish-flow-step-marker">
                {{ sectionIndex + 1 }}
              </div>
              <div class="publish-stage-flow-connector" data-testid="publish-flow-step-connector" />
            </div>
            <div class="publish-stage-flow-content">
              <div
                class="publish-flow-business-section"
                :data-business-section="section.id"
                :data-testid="`publish-flow-business-section-${section.id}`"
              >
            <div class="publish-flow-business-section-header">
              <div class="d-flex align-center flex-wrap ga-2">
                <div class="text-subtitle-1 font-weight-bold">{{ section.label }}</div>
                <v-spacer />
                <v-chip size="small" :color="section.statusColor" variant="tonal">
                  {{ section.statusLabel }}
                </v-chip>
              </div>
              <div
                v-if="section.summaryLine"
                class="text-body-2 mt-1 publish-stage-outcome"
                :data-testid="`publish-flow-business-section-summary-${section.id}`"
              >
                {{ section.summaryLine }}
              </div>
              <div
                v-if="section.handoffLine"
                class="text-caption text-medium-emphasis mt-1 publish-stage-handoff"
                :data-testid="`publish-flow-business-section-handoff-${section.id}`"
              >
                {{ section.handoffLine }}
              </div>
            </div>
            <div class="publish-stage-actions d-flex align-center flex-wrap ga-2">
              <template v-for="stage in section.subStages" :key="`action-${stage.stage}`">
                <v-btn
                  v-if="
                    stage.hasDetails &&
                    (stage.stage !== 'listingRules' || stage.detailButtonLabel === 'View Matched Rules')
                  "
                  size="small"
                  variant="tonal"
                  color="primary"
                  class="align-self-start"
                  :data-testid="`publish-flow-stage-action-${stage.stage}`"
                  @click.stop="onOpenDetailedImpactForStage(stage.stage)"
                >
                  {{ stage.detailButtonLabel ?? 'View details' }}
                </v-btn>
              </template>
              <v-btn
                v-if="section.id === 'inventorySync' && inventorySyncHeldBackCount > 0"
                size="small"
                variant="tonal"
                color="primary"
                class="align-self-start"
                data-testid="publish-flow-inventory-sync-held-back-action"
                @click.stop="openInventorySyncHeldBack"
              >
                View Held Back
              </v-btn>
              <v-btn
                v-if="section.showExpandedDetails"
                size="small"
                variant="text"
                color="primary"
                class="px-0 publish-stage-details-toggle"
                :data-testid="`publish-flow-section-details-toggle-${section.id}`"
                @click="togglePublishStageSectionDetails(section.id)"
              >
                <span>
                  {{
                    isPublishStageSectionExpanded(section.id)
                      ? 'Hide details'
                      : 'More details'
                  }}
                </span>
                <v-icon
                  :icon="
                    isPublishStageSectionExpanded(section.id)
                      ? 'mdi-chevron-up'
                      : 'mdi-chevron-down'
                  "
                  size="16"
                  class="ml-1"
                />
              </v-btn>
            </div>
            <Transition name="publish-stage-details">
              <div
                v-show="isPublishStageSectionExpanded(section.id)"
                class="publish-stage-expanded-details"
                :data-testid="`publish-flow-section-details-${section.id}`"
              >
            <v-row
              v-if="section.id === 'inventorySync'"
              data-testid="publish-flow-inventory-sync-details-row"
            >
              <v-col cols="12">
                <v-card
                  class="sub-stage-card h-100"
                  elevation="0"
                  data-stage="inventorySync"
                  data-testid="publish-flow-inventory-sync-details-card"
                >
                  <v-card-text class="stage-card-inner d-flex flex-column ga-2">
                    <div class="d-flex align-center ga-2">
                      <div class="text-subtitle-2 font-weight-bold">Inventory Sync</div>
                      <v-spacer />
                      <v-chip
                        size="x-small"
                        :color="section.statusColor"
                        variant="outlined"
                        class="sub-stage-status-chip"
                      >
                        {{ section.statusLabel }}
                      </v-chip>
                    </div>
                    <InventorySyncSectionPanel
                      ref="inventorySyncSectionPanelRef"
                      :funnel="inventorySyncFunnel"
                      :endpoint="endpoint"
                      :jazel-id="accountStore.accountId"
                      :correlation-id="correlationId"
                      :session-token="accountStore.sessionToken"
                    />
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-else-if="section.id === 'fieldMapping'">
              <v-col cols="12">
                <v-card
                  v-for="stage in section.subStages"
                  :key="stage.stage"
                  class="sub-stage-card"
                  elevation="0"
                  :data-stage="stage.stage"
                  data-testid="publish-flow-field-mapping-vehicle-data-card"
                >
                  <v-card-text class="stage-card-inner d-flex flex-column ga-2">
                    <div class="d-flex align-center ga-2">
                      <div class="d-flex align-center ga-1">
                        <div class="text-subtitle-2 font-weight-bold">Vehicle Data Mapping</div>
                        <v-tooltip
                          v-if="stage.helpText"
                          location="top"
                          max-width="320"
                          :text="stage.helpText"
                        >
                          <template #activator="{ props: tooltipProps }">
                            <v-icon
                              v-bind="tooltipProps"
                              icon="mdi-information-outline"
                              size="16"
                              color="medium-emphasis"
                              class="cursor-help"
                              :aria-label="`${stage.label} information`"
                            />
                          </template>
                        </v-tooltip>
                      </div>
                      <v-spacer />
                      <v-chip
                        size="x-small"
                        :color="stage.statusColor"
                        variant="outlined"
                        class="sub-stage-status-chip"
                      >
                        {{ stage.statusLabel }}
                      </v-chip>
                    </div>
                    <div v-if="stage.summaryLine" class="text-body-2">{{ stage.summaryLine }}</div>

                    <div v-if="stage.outcomeLines?.length" class="d-flex flex-column ga-1">
                      <div
                        v-for="(line, i) in stage.outcomeLines"
                        :key="`outcome-${stage.stage}-${i}`"
                        class="text-body-2"
                      >
                        {{ line }}
                      </div>
                    </div>

                    <div
                      v-else-if="stage.inputDetail || stage.resultDetail || stage.nextDetail"
                      class="d-flex flex-column ga-1"
                    >
                      <div v-if="stage.inputDetail" class="text-body-2">
                        <span class="text-medium-emphasis">Input:</span> {{ stage.inputDetail }}
                      </div>
                      <div v-if="stage.resultDetail" class="text-body-2">
                        <span class="text-medium-emphasis">Result:</span> {{ stage.resultDetail }}
                      </div>
                      <div v-if="stage.nextDetail" class="text-body-2">
                        <span class="text-medium-emphasis">Next:</span> {{ stage.nextDetail }}
                      </div>
                    </div>
                    <template
                      v-if="
                        stage.stage === 'preProcessor' &&
                        (preProcessorFeedSourceCounts.length || stage.moreDetails?.length)
                      "
                    >
                      <div
                        class="d-flex flex-column ga-1"
                        :data-testid="`publish-flow-more-details-${stage.stage}`"
                      >
                        <template v-if="preProcessorFeedSourceCounts.length">
                          <div
                            v-for="fc in preProcessorFeedSourceCounts"
                            :key="`preProcessor-feed-${fc.feed}`"
                            class="text-body-2 text-medium-emphasis d-flex align-center flex-wrap ga-2"
                          >
                            <span>{{ formatPreProcessorFeedSourceCountLine(fc) }}</span>
                            <v-btn
                              v-if="preProcessorFeedSourceFileDownloadable(fc)"
                              size="x-small"
                              variant="text"
                              color="primary"
                              class="px-0"
                              :loading="isPreProcessorFeedSourceFileDownloading(fc)"
                              :data-testid="`publish-flow-preprocessor-feed-file-${fc.feed}`"
                              @click.stop="downloadPreProcessorFeedSourceFile(fc)"
                            >
                              View feed file
                            </v-btn>
                          </div>
                          <div
                            v-if="multiFeedCountReconciliationLine"
                            class="text-caption text-medium-emphasis"
                            data-testid="publish-flow-preprocessor-count-reconciliation"
                          >
                            {{ multiFeedCountReconciliationLine }}
                          </div>
                        </template>
                        <div
                          v-for="(line, i) in preProcessorFeedSourceCounts.length
                            ? preProcessorNonFeedMoreDetails(stage.moreDetails)
                            : stage.moreDetails"
                          :key="'detail-' + i"
                          class="text-body-2 text-medium-emphasis"
                        >
                          {{ line }}
                        </div>
                      </div>
                    </template>

                    <template v-else-if="stage.moreDetails?.length">
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        class="align-self-start px-0"
                        :data-testid="`publish-flow-more-details-toggle-${stage.stage}`"
                        @click="toggleStageMoreDetails(stage.stage)"
                      >
                        {{
                          isStageMoreDetailsExpanded(stage.stage)
                            ? 'Hide more details'
                            : 'More details'
                        }}
                      </v-btn>
                      <div
                        v-if="isStageMoreDetailsExpanded(stage.stage)"
                        class="d-flex flex-column ga-1 pl-2"
                        :data-testid="`publish-flow-more-details-${stage.stage}`"
                      >
                        <div
                          v-for="(line, i) in stage.moreDetails"
                          :key="'detail-' + i"
                          class="text-body-2 text-medium-emphasis"
                        >
                          {{ line }}
                        </div>
                      </div>
                    </template>

                    <div
                      v-if="stage.feedFieldRulesSubsection"
                      class="feed-field-rules-nested-subsection pt-2"
                      :class="`feed-field-rules-nested-subsection--${feedFieldRulesSubsectionEmphasisClass(stage.feedFieldRulesSubsection)}`"
                      data-testid="publish-flow-field-mapping-feed-field-rules"
                    >
                      <v-divider class="mb-3" />
                      <div class="text-body-2 font-weight-medium">Feed Field Rules</div>
                      <div
                        class="text-body-2 mt-1"
                        :class="
                          feedFieldRulesSubsectionEmphasisClass(stage.feedFieldRulesSubsection) ===
                          'quiet'
                            ? 'text-medium-emphasis'
                            : ''
                        "
                      >
                        {{ stage.feedFieldRulesSubsection.summaryLine }}
                      </div>
                      <v-btn
                        v-if="stage.feedFieldRulesSubsection.hasDetails"
                        size="small"
                        variant="text"
                        color="primary"
                        class="align-self-start px-0 mt-1"
                        data-testid="publish-flow-feed-field-rules-view-details"
                        @click="onOpenDetailedImpactForStage('feedFieldRules')"
                      >
                        View feed field rule details
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-else-if="section.id === 'vehicleGroupings'">
              <v-col cols="12">
                <v-card
                  v-for="stage in section.subStages"
                  :key="stage.stage"
                  class="sub-stage-card h-100"
                  elevation="0"
                  :data-stage="stage.stage"
                  data-testid="publish-flow-vehicle-groupings-details-card"
                >
                  <v-card-text class="stage-card-inner d-flex flex-column ga-2">
                    <div class="d-flex align-center ga-2">
                      <div class="text-subtitle-2 font-weight-bold">{{ stage.label }}</div>
                      <v-spacer />
                      <v-chip
                        size="x-small"
                        :color="stage.statusColor"
                        variant="outlined"
                        class="sub-stage-status-chip"
                      >
                        {{ stage.statusLabel }}
                      </v-chip>
                    </div>

                    <div v-if="stage.outcomeLines?.length" class="d-flex flex-column ga-1">
                      <div
                        v-for="(line, i) in stage.outcomeLines"
                        :key="`groups-outcome-${stage.stage}-${i}`"
                        class="text-body-2"
                      >
                        {{ line }}
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col
                v-for="stage in section.subStages"
                :key="stage.stage"
                cols="12"
                :md="section.subStages.length === 1 ? 12 : 6"
                :lg="section.subStages.length === 1 ? 12 : 4"
              >
                <v-card class="sub-stage-card h-100" elevation="0" :data-stage="stage.stage">
                  <v-card-text class="stage-card-inner d-flex flex-column ga-2">
                    <div class="d-flex align-center ga-2">
                      <div class="d-flex align-center ga-1">
                        <div class="text-subtitle-2 font-weight-bold">{{ stage.label }}</div>
                        <v-tooltip
                          v-if="stage.helpText"
                          location="top"
                          max-width="320"
                          :text="stage.helpText"
                        >
                          <template #activator="{ props: tooltipProps }">
                            <v-icon
                              v-bind="tooltipProps"
                              icon="mdi-information-outline"
                              size="16"
                              color="medium-emphasis"
                              class="cursor-help"
                              :aria-label="`${stage.label} information`"
                            />
                          </template>
                        </v-tooltip>
                      </div>
                      <v-spacer />
                      <v-chip
                        size="x-small"
                        :color="stage.statusColor"
                        variant="outlined"
                        class="sub-stage-status-chip"
                      >
                        {{ stage.statusLabel }}
                      </v-chip>
                    </div>

                    <div v-if="stage.summaryLine" class="text-body-2">{{ stage.summaryLine }}</div>

                    <div v-if="stage.outcomeLines?.length" class="d-flex flex-column ga-1">
                      <div
                        v-for="(line, i) in stage.outcomeLines"
                        :key="`outcome-${stage.stage}-${i}`"
                        class="text-body-2"
                      >
                        {{ line }}
                      </div>
                    </div>

                    <div
                      v-else-if="stage.inputDetail || stage.resultDetail || stage.nextDetail"
                      class="d-flex flex-column ga-1"
                    >
                      <div v-if="stage.inputDetail" class="text-body-2">
                        <span class="text-medium-emphasis">Input:</span> {{ stage.inputDetail }}
                      </div>
                      <div v-if="stage.resultDetail" class="text-body-2">
                        <span class="text-medium-emphasis">Result:</span> {{ stage.resultDetail }}
                      </div>
                      <div v-if="stage.nextDetail" class="text-body-2">
                        <span class="text-medium-emphasis">Next:</span> {{ stage.nextDetail }}
                      </div>
                    </div>

                    <template
                      v-if="
                        stage.stage === 'preProcessor' &&
                        (preProcessorFeedSourceCounts.length || stage.moreDetails?.length)
                      "
                    >
                      <div
                        class="d-flex flex-column ga-1"
                        :data-testid="`publish-flow-more-details-${stage.stage}`"
                      >
                        <template v-if="preProcessorFeedSourceCounts.length">
                          <div
                            v-for="fc in preProcessorFeedSourceCounts"
                            :key="`preProcessor-feed-${fc.feed}`"
                            class="text-body-2 text-medium-emphasis d-flex align-center flex-wrap ga-2"
                          >
                            <span>{{ formatPreProcessorFeedSourceCountLine(fc) }}</span>
                            <v-btn
                              v-if="preProcessorFeedSourceFileDownloadable(fc)"
                              size="x-small"
                              variant="text"
                              color="primary"
                              class="px-0"
                              :loading="isPreProcessorFeedSourceFileDownloading(fc)"
                              :data-testid="`publish-flow-preprocessor-feed-file-${fc.feed}`"
                              @click.stop="downloadPreProcessorFeedSourceFile(fc)"
                            >
                              View feed file
                            </v-btn>
                          </div>
                          <div
                            v-if="multiFeedCountReconciliationLine"
                            class="text-caption text-medium-emphasis"
                            data-testid="publish-flow-preprocessor-count-reconciliation"
                          >
                            {{ multiFeedCountReconciliationLine }}
                          </div>
                        </template>
                        <div
                          v-for="(line, i) in preProcessorFeedSourceCounts.length
                            ? preProcessorNonFeedMoreDetails(stage.moreDetails)
                            : stage.moreDetails"
                          :key="'detail-' + i"
                          class="text-body-2 text-medium-emphasis"
                        >
                          {{ line }}
                        </div>
                      </div>
                    </template>

                    <template v-else-if="stage.moreDetails?.length && stage.stage !== 'listingRules'">
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        class="align-self-start px-0"
                        :data-testid="`publish-flow-more-details-toggle-${stage.stage}`"
                        @click="toggleStageMoreDetails(stage.stage)"
                      >
                        {{
                          isStageMoreDetailsExpanded(stage.stage)
                            ? 'Hide more details'
                            : 'More details'
                        }}
                      </v-btn>
                      <div
                        v-if="isStageMoreDetailsExpanded(stage.stage)"
                        class="d-flex flex-column ga-1 pl-2"
                        :data-testid="`publish-flow-more-details-${stage.stage}`"
                      >
                        <div
                          v-for="(line, i) in stage.moreDetails"
                          :key="'detail-' + i"
                          class="text-body-2 text-medium-emphasis"
                        >
                          {{ line }}
                        </div>
                      </div>
                    </template>

                    <template v-if="stage.stage === 'listingRules' && stage.ruleResultLines?.length">
                      <div class="text-body-2 text-medium-emphasis">Blocking rule matches</div>
                      <div
                        v-for="(line, i) in stage.ruleResultLines"
                        :key="'rule-inline-' + i"
                        class="text-body-2 text-medium-emphasis"
                        :data-testid="`publish-flow-rule-match-line-listingRules-${i}`"
                      >
                        {{ line }}
                      </div>
                    </template>
                    <template v-else-if="stage.ruleResultLines?.length">
                      <v-btn
                        size="small"
                        variant="text"
                        color="primary"
                        class="align-self-start px-0"
                        :data-testid="`publish-flow-rule-details-toggle-${stage.stage}`"
                        @click="toggleRuleDetails(stage.stage)"
                      >
                        {{
                          isRuleDetailsExpanded(stage.stage)
                            ? 'Hide rule details'
                            : 'Show rule details'
                        }}
                      </v-btn>
                      <div
                        v-if="isRuleDetailsExpanded(stage.stage)"
                        class="d-flex flex-column ga-1 pl-2"
                        :data-testid="`publish-flow-rule-details-${stage.stage}`"
                      >
                        <div
                          v-for="(line, i) in stage.ruleResultLines"
                          :key="'rule-' + i"
                          class="text-body-2 text-medium-emphasis"
                        >
                          {{ line }}
                        </div>
                      </div>
                    </template>

                    <v-btn
                      v-if="stage.hasDetails && stage.stage !== 'groups' && stage.stage !== 'listingRules'"
                      size="small"
                      variant="tonal"
                      color="primary"
                      class="align-self-start"
                      @click.stop="onOpenDetailedImpactForStage(stage.stage)"
                    >
                      {{ stage.detailButtonLabel ?? 'View details' }}
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
              </div>
            </Transition>
          </div>
            </div>
          </div>

          <!--
            Website Publish & Refresh — final publish stage (presentation-only).
            Website Visibility Changes re-presents Run Summary outcome deltas;
            Website Refresh renders captured cache refresh details. No publish or
            invalidation behavior is triggered here.
          -->
          <div
            class="publish-stage-flow-row publish-stage-flow-row--last"
            data-testid="publish-flow-step"
            :data-step-index="publishFlowBusinessSections.length + 1"
          >
            <div class="publish-stage-flow-rail" aria-hidden="true">
              <div class="publish-stage-flow-marker" data-testid="publish-flow-step-marker">
                {{ publishFlowBusinessSections.length + 1 }}
              </div>
            </div>
            <div class="publish-stage-flow-content">
              <div
                class="publish-flow-business-section"
                data-business-section="websitePublishRefresh"
                data-testid="publish-flow-website-publish-refresh"
              >
            <div class="publish-flow-business-section-header">
              <div class="d-flex align-center flex-wrap ga-2">
                <div class="text-subtitle-1 font-weight-bold">Website Publish &amp; Refresh</div>
                <v-spacer />
                <v-chip
                  size="small"
                  :color="websitePublishRefreshStatus.color"
                  variant="tonal"
                >
                  {{ websitePublishRefreshStatus.label }}
                </v-chip>
              </div>
              <div
                class="text-body-2 mt-1 publish-stage-outcome"
                data-testid="publish-flow-business-section-summary-websitePublishRefresh"
              >
                {{ websitePublishRefreshSummaryLine }}
              </div>
            </div>
            <div class="publish-stage-actions d-flex align-center flex-wrap ga-2">
              <v-btn
                size="small"
                variant="text"
                color="primary"
                class="px-0 publish-stage-details-toggle"
                data-testid="publish-flow-section-details-toggle-websitePublishRefresh"
                @click="togglePublishStageSectionDetails('websitePublishRefresh')"
              >
                <span>
                  {{
                    isPublishStageSectionExpanded('websitePublishRefresh')
                      ? 'Hide details'
                      : 'More details'
                  }}
                </span>
                <v-icon
                  :icon="
                    isPublishStageSectionExpanded('websitePublishRefresh')
                      ? 'mdi-chevron-up'
                      : 'mdi-chevron-down'
                  "
                  size="16"
                  class="ml-1"
                />
              </v-btn>
            </div>
            <Transition name="publish-stage-details">
              <div
                v-show="isPublishStageSectionExpanded('websitePublishRefresh')"
                class="publish-stage-expanded-details"
                data-testid="publish-flow-section-details-websitePublishRefresh"
              >
            <v-row>
              <v-col cols="12" md="6">
                <v-card
                  class="sub-stage-card h-100"
                  elevation="0"
                  data-testid="publish-flow-website-visibility-changes"
                >
                  <v-card-text class="stage-card-inner d-flex flex-column ga-2">
                    <div class="text-subtitle-2 font-weight-bold">Website Visibility Changes</div>
                    <div class="text-body-2">{{ websiteVisibilityChanges.headline }}</div>
                    <div
                      v-if="websiteVisibilityChanges.deltaLine"
                      class="text-body-2 text-grey"
                    >
                      {{ websiteVisibilityChanges.deltaLine }}
                    </div>
                    <div
                      v-if="websiteVisibilityChanges.detailsNote"
                      class="text-caption text-grey"
                      data-testid="publish-flow-website-visibility-changes-note"
                    >
                      {{ websiteVisibilityChanges.detailsNote }}
                    </div>
                    <v-btn
                      v-if="websiteVisibilityChanges.showViewChanges"
                      size="small"
                      variant="tonal"
                      color="primary"
                      class="align-self-start"
                      data-testid="publish-flow-website-visibility-changes-view"
                      @click="onOpenDetailedImpactForStage('listingRules')"
                    >
                      View changes
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6">
                <v-card
                  class="sub-stage-card h-100"
                  elevation="0"
                  data-testid="publish-flow-website-refresh"
                >
                  <v-card-text class="stage-card-inner d-flex flex-column ga-2">
                    <div class="d-flex align-center ga-2">
                      <div class="text-subtitle-2 font-weight-bold">Website Refresh</div>
                      <v-spacer />
                      <v-chip
                        size="x-small"
                        :color="websiteRefresh.statusColor"
                        variant="outlined"
                        class="sub-stage-status-chip"
                      >
                        {{ websiteRefresh.statusLabel }}
                      </v-chip>
                    </div>
                    <div
                      v-for="(line, i) in websiteRefresh.lines"
                      :key="`website-refresh-line-${i}`"
                      class="text-body-2"
                      :class="{ 'text-grey': websiteRefresh.status === 'not_captured' || i > 0 }"
                    >
                      {{ line }}
                    </div>
                    <div
                      v-if="websiteRefresh.keyTypeBreakdown"
                      class="text-body-2 text-grey"
                      data-testid="publish-flow-website-refresh-key-breakdown"
                    >
                      {{ websiteRefresh.keyTypeBreakdown }}
                    </div>
                    <div
                      v-if="websiteRefresh.keysHelperText"
                      class="text-caption text-grey"
                      data-testid="publish-flow-website-refresh-keys-helper"
                    >
                      {{ websiteRefresh.keysHelperText }}
                    </div>
                    <div
                      v-if="websiteRefresh.metadata.length"
                      class="website-refresh-metadata mt-1"
                    >
                      <div
                        v-for="row in websiteRefresh.metadata"
                        :key="`website-refresh-meta-${row.label}`"
                        class="text-caption text-grey"
                      >
                        <span class="font-weight-medium">{{ row.label }}:</span> {{ row.value }}
                      </div>
                    </div>
                    <v-btn
                      v-if="websiteRefresh.showRefreshedKeys"
                      size="small"
                      variant="tonal"
                      color="primary"
                      class="align-self-start mt-1"
                      data-testid="publish-flow-website-refresh-view-keys"
                      @click="openWebsiteRefreshKeysDialog"
                    >
                      View refreshed keys
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
              </div>
            </Transition>
          </div>
            </div>
          </div>
          </div>
        </v-card-text>
      </v-card>

      <!--
        Phase 6.y — Detailed Publish Impact dialog.
        v-if-gated on manifest ok + listingRules pointer present + auth
        (Adjustment #2). The store owns its own `dialogOpen` flag so
        clicking the card's button toggles visibility from inside this
        subtree without touching local view state.
      -->
      <DetailedPublishImpactDialog
        v-if="detailedImpactReady"
        :endpoint="endpoint"
        :jazel-id="accountStore.accountId"
        :correlation-id="correlationId"
        :session-token="accountStore.sessionToken"
        :stage-detail-lines="detailedPublishStageDetailLines"
        :stage-detail-actions="detailedPublishStageDetailActions"
        :authoritative-visibility="detailedPublishAuthoritativeVisibility"
        @stage-detail-action="onDetailedPublishStageAction"
      />

      <v-dialog v-model="websiteRefreshKeysDialogOpen" max-width="900">
        <v-card data-testid="publish-flow-website-refresh-keys-dialog">
          <v-card-title class="d-flex align-center ga-2">
            Refreshed Cache Keys
            <v-spacer />
            <v-btn
              icon
              variant="text"
              aria-label="Close refreshed cache keys dialog"
              @click="websiteRefreshKeysDialogOpen = false"
            >
              <v-icon icon="mdi-close" />
            </v-btn>
          </v-card-title>
          <v-card-text class="d-flex flex-column ga-3">
            <v-text-field
              v-model="websiteRefreshKeySearch"
              label="Search key or VIN"
              density="compact"
              variant="outlined"
              clearable
              hide-details
              data-testid="publish-flow-website-refresh-keys-search"
            />
            <div
              v-if="websiteRefreshKeysLoading && websiteRefreshKeyRows.length === 0"
              class="text-body-2 text-grey"
              data-testid="publish-flow-website-refresh-keys-loading"
            >
              Loading refreshed cache keys…
            </div>
            <div
              v-else-if="websiteRefreshKeysUnavailable && websiteRefreshKeyRows.length === 0"
              class="text-body-2 text-grey"
              data-testid="publish-flow-website-refresh-keys-unavailable"
            >
              Refreshed cache keys are temporarily unavailable. Please try again.
            </div>
            <div
              v-else-if="websiteRefreshKeyRows.length === 0 && websiteRefreshKeysSearchActive"
              class="text-body-2 text-grey"
              data-testid="publish-flow-website-refresh-keys-empty"
            >
              No refreshed keys match this search.
            </div>
            <div
              v-else-if="websiteRefreshKeyRows.length === 0"
              class="text-body-2 text-grey"
              data-testid="publish-flow-website-refresh-keys-none"
            >
              No refreshed cache keys were captured for this run.
            </div>
            <template v-else>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th class="text-left">Type</th>
                    <th class="text-left">Key</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in websiteRefreshKeyRows"
                    :key="row.key"
                    data-testid="publish-flow-website-refresh-key-row"
                  >
                    <td>{{ websiteRefreshCacheKeyTypeLabel(row.type) }}</td>
                    <td class="website-refresh-key-cell text-truncate" :title="row.key">
                      {{ row.key }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
              <div class="d-flex align-center ga-2">
                <v-btn
                  v-if="websiteRefreshKeysHasMore"
                  size="small"
                  variant="tonal"
                  :loading="websiteRefreshKeysLoading"
                  data-testid="publish-flow-website-refresh-keys-load-more"
                  @click="loadMoreWebsiteRefreshKeys"
                >
                  Load more
                </v-btn>
                <span
                  v-else
                  class="text-caption text-grey"
                  data-testid="publish-flow-website-refresh-keys-end"
                >
                  Showing all {{ websiteRefreshKeysTotal }} refreshed
                  {{ websiteRefreshKeysTotal === 1 ? 'key' : 'keys' }}.
                </span>
              </div>
            </template>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Raw observability (JSON only; collapsed by default — Publish Stages is the human-readable flow) -->
      <v-card
        class="border mb-4 publish-flow-raw-observability"
        elevation="0"
        data-testid="publish-flow-raw-observability"
      >
        <v-card-text class="py-3 px-4">
          <div class="d-flex align-center flex-wrap ga-3">
            <div class="flex-grow-1 min-width-0">
              <div class="text-subtitle-2 font-weight-medium">Raw Observability</div>
              <div class="text-body-2 text-medium-emphasis mt-1">
                Technical JSON logs for this publish run.
              </div>
            </div>
            <v-btn
              size="small"
              variant="text"
              color="primary"
              class="px-0 align-self-start"
              data-testid="publish-flow-raw-observability-toggle"
              @click="rawObservabilityExpanded = !rawObservabilityExpanded"
            >
              {{ rawObservabilityExpanded ? 'Hide JSON' : 'Show JSON' }}
            </v-btn>
          </div>

          <div
            v-if="rawObservabilityExpanded"
            class="mt-3"
            data-testid="publish-flow-raw-observability-json"
          >
            <div v-if="!result?.logs && !errors.logs" class="text-body-2 text-grey">
              No logs found for this publish.
            </div>
            <PublishLogsTimeline
              v-else-if="result?.logs"
              :logs="[]"
              :raw-json="rawLogsJson"
              :error="errors.logs || ''"
              :json-only="true"
            />
            <v-alert
              v-if="errors.logs && !result?.logs"
              type="warning"
              variant="tonal"
              density="comfortable"
              class="mt-3"
            >
              Logs lookup failed: {{ errors.logs }}
            </v-alert>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <v-snackbar
      v-model="showPublishFlowFileError"
      color="error"
      location="bottom"
      timeout="6000"
      data-testid="publish-flow-file-error-snackbar"
    >
      {{ publishFlowFileError }}
    </v-snackbar>
  </v-container>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'
import PublishLogsTimeline from '@/components/PublishLogsTimeline.vue'
import ChangeBucketCard from '@/components/publishFlow/ChangeBucketCard.vue'
import DetailedPublishImpactDialog, {
  type DetailedPublishStageAction,
} from '@/components/publishFlow/DetailedPublishImpactDialog.vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useDetailedPublishImpactStore } from '@/stores/useDetailedPublishImpactStore'
import {
  buildInventoryChanges,
  buildPublishOutcome,
  selectCompleteRunMetrics,
  buildRunSummary,
  countUniqueChangedWebsiteVins,
  fetchPublishFlow,
  type PublishFlowFetchResult,
  type RunSummaryViewModel,
} from '@/services/publishFlowService'
import {
  buildFeedIntakeRows,
  buildVisibilityRuleRows,
  buildPublishEligibilityExpandedRuleMatchLines,
  getFlowPreProcessorDroppedCount,
  getFlowPreProcessorInputCount,
  getFlowPreProcessorOutputCount,
  getFlowPreProcessorFeedSourceCounts,
  getFlowPreProcessorParticipatingFeeds,
  isFlowPreProcessorNotNeeded,
  isFlowPreProcessorSummary,
  type FlowPreProcessor,
  type FlowFeedSourceCount,
  type FlowMetricsArtifact,
  type FlowBlockedClassification,
  type FlowVisibilityListingRules,
  type VisibilityRuleRowViewModel,
} from '@/services/publishFlowMetricsService'
import { formatFeedSource } from '@/utils/feedSourceFormat'
import {
  preProcessorSourceFilePointer,
  publishFlowFileCacheKey,
  publishFlowFileUnavailableMessage,
  PublishFlowFilePresignError,
  openExternalDownloadUrl,
  requestPublishFlowFilePresign,
  resolvePreProcessorOutputFilePointer,
  type PublishFlowFileRole,
} from '@/services/publishFlowFilePresignService'
import {
  fetchPublishFlowObservability,
  type PublishObservedStage,
  type PublishFlowObservabilityFetch,
  type PublishStageSummary,
  type PublishFlowStageDetailFunnel,
} from '@/services/publishFlowObservabilityService'
import {
  tryGetWebsiteRefreshKeys,
  type WebsiteRefreshKeyRecord,
  type WebsiteRefreshKeyType,
} from '@/services/publishFlowWebsiteRefreshKeysService'
import {
  aggregateBusinessSectionStatus,
  aggregateWebsitePublishRefreshStatus,
  buildBusinessSectionSummaryLine,
  buildInventorySyncCardViewModel,
  buildWebsitePublishRefreshSummaryLine,
  buildWebsiteRefreshViewModel,
  buildWebsiteVisibilityChangesViewModel,
  friendlyPublishFlowBusinessSectionLabel,
  PUBLISH_FLOW_BUSINESS_SECTIONS,
  stageRollupSignal,
  type BusinessSectionSummaryContext,
  type PublishFlowBusinessSectionId,
  type WebsiteRefreshDetails,
} from '@/utils/publishFlowBusinessSections'
import InventorySyncSectionPanel from '@/components/publishFlow/InventorySyncSectionPanel.vue'
import {
  PUBLISH_FLOW_PAGE_STAGE_ORDER,
  formatPublishFlowMovedToStageCopy,
  friendlyPublishFlowStageLabel,
  publishFlowFeedFieldRulesSubsectionSummaryCopy,
  publishFlowGroupsChangedResultCopy,
  publishFlowStageChipColor,
  publishFlowStageChipLabel,
  publishFlowPreProcessorCardInputCopy,
  publishFlowPreProcessorCardResultCopy,
  publishFlowPreProcessorCardSummaryCopy,
} from '@/utils/publishFlowStageLabels'

type PreProcessorSourceRow = NonNullable<
  NonNullable<FlowMetricsArtifact['publishWide']['preProcessor']>['perSourceCounts']
>[number]

const route = useRoute()
const router = useRouter()
const accountStore = useAccountSettingsStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL as string

const correlationId = computed(() => String(route.params.correlationId || '').trim())

const isLoading = ref(false)
const rawObservabilityExpanded = ref(false)
const result = ref<PublishFlowFetchResult | null>(null)
const aggregatedErrorMessage = ref('')
const downloadingPublishFlowFileKey = ref('')
const unavailablePublishFlowFiles = ref<Set<string>>(new Set())
const publishFlowFileError = ref('')
const showPublishFlowFileError = ref(false)

// Phase 5.y — additive observability probe; independent of `loadFlow` so the
// new authenticated endpoint never alters legacy aggregator behavior.
const observability = ref<PublishFlowObservabilityFetch | null>(null)

// Phase 6.y — Detailed Publish Impact dialog state. The store is shared
// between the card (button) and the dialog (model-value); the view's
// only role is to call `openDialog(manifest)` when the card emits.
const detailedPublishImpactStore = useDetailedPublishImpactStore()

const currentManifest = computed(() =>
  observability.value?.manifest.status === 'ok' ? observability.value.manifest.data : null,
)

const currentSummary = computed(() =>
  observability.value?.summary.status === 'ok' ? observability.value.summary.data : null,
)

type StageDetailStatus =
  | 'available'
  | 'empty'
  | 'notConfigured'
  | 'skipped'
  | 'unsupported'
  | 'error'

const STAGE_FALLBACK_IO_LINE = 'Input/output details are not captured yet.'
/** Inner-card title for the Publish Rules Evaluation stage (formerly "Website Visibility"). */
const PUBLISH_ELIGIBILITY_LABEL = 'Publish Eligibility'
type FeedFieldRulesSubsectionViewModel = {
  summaryLine: string
  hasDetails: boolean
  status: StageDetailStatus
  changedCount: number
}

type FeedFieldRulesSubsectionEmphasis = 'quiet' | 'noticeable' | 'attention'

function feedFieldRulesSubsectionEmphasisClass(
  subsection: FeedFieldRulesSubsectionViewModel,
): FeedFieldRulesSubsectionEmphasis {
  if (subsection.status === 'error') return 'attention'
  if (subsection.changedCount > 0) return 'noticeable'
  return 'quiet'
}

type CanonicalStageCard = {
  stage: PublishObservedStage
  label: string
  helpText?: string
  statusLabel: string
  statusColor: string
  detailStatus: StageDetailStatus
  hasDetails: boolean
  /** Label for the drilldown button. Defaults to "View details" in the template. */
  detailButtonLabel?: string
  /** Plain-English one-line outcome shown beneath the title. */
  summaryLine: string
  /** Stage-specific scan-friendly lines. Used when Input/Result/Next is too technical. */
  outcomeLines?: string[]
  inputDetail?: string
  resultDetail?: string
  nextDetail?: string
  /** Extra measured telemetry shown only when the user expands stage details. */
  moreDetails?: string[]
  /** Friendly rule match lines; shown only when expanded. */
  ruleResultLines?: string[]
  /** Nested Feed Field Rules block inside Field Mapping (Preview-aligned). */
  feedFieldRulesSubsection?: FeedFieldRulesSubsectionViewModel
  /** Observability explanation for rare supplemental copy (mostly pre-processor). */
  observabilityExplanation: string
}

const canonicalStageCards = computed<CanonicalStageCard[]>(() =>
  PUBLISH_FLOW_PAGE_STAGE_ORDER.map((stage) => buildCanonicalStageCard(stage)),
)

const canonicalStageCardsByKey = computed(() => {
  const map = new Map<PublishObservedStage, CanonicalStageCard>()
  for (const card of canonicalStageCards.value) {
    map.set(card.stage, card)
  }
  return map
})

type PublishFlowBusinessSectionViewModel = {
  id: PublishFlowBusinessSectionId
  label: string
  summaryLine: string | null
  handoffLine: string | null
  statusLabel: string
  statusColor: string
  subStages: CanonicalStageCard[]
  /** When false, the section compact row omits the More details affordance. */
  showExpandedDetails: boolean
}

type PublishStageSectionId = PublishFlowBusinessSectionId | 'websitePublishRefresh'

function preProcessorSourceVinTotal(): number | null {
  const rows = preProcessorSourceRowsFull.value
  if (rows.length === 0) return null
  let total = 0
  let hasCount = false
  for (const row of rows) {
    if (typeof row.vehicleCount === 'number' && Number.isFinite(row.vehicleCount)) {
      total += Math.max(0, Math.floor(row.vehicleCount))
      hasCount = true
    }
  }
  return hasCount ? total : null
}

function resolveEligibilitySourceVinCount(): number | null {
  const metricsInput = getFlowPreProcessorInputCount(preProcessorMetrics.value)
  if (typeof metricsInput === 'number' && Number.isFinite(metricsInput)) {
    return Math.max(0, Math.floor(metricsInput))
  }
  return preProcessorSourceVinTotal()
}

function resolveFieldMappingVehiclesChecked(): number | null {
  const details = stageSummaryFor('fieldMapping')?.details as
    | PublishFlowObservabilityServiceDetails
    | undefined
  const nIn = numericCountFromStructured(details?.input)
  return upstreamVehicleCount('fieldMapping') ?? (typeof nIn === 'number' ? nIn : null)
}

function resolvePublishCandidateMovementCount(): number | null {
  const funnel = resolveInventorySyncFunnel()
  const inventorySyncCount =
    finiteNumber(funnel?.handoffCount) ?? finiteNumber(funnel?.continuedCount)
  if (inventorySyncCount !== null) return Math.max(0, Math.floor(inventorySyncCount))

  const rulesEvaluated = finiteNumber(visibilityTotals.value?.evaluatedCount)
  if (rulesEvaluated !== null) return Math.max(0, Math.floor(rulesEvaluated))

  const fieldMappingInput = resolveFieldMappingVehiclesChecked()
  return fieldMappingInput !== null ? Math.max(0, Math.floor(fieldMappingInput)) : null
}

function resolveFieldMappingDecisionsCaptured(): number | null {
  const summary = stageSummaryFor('fieldMapping')

  // For Publish Flow, fieldMapping is decision-artifact backed.
  // Backend aggregate counts.changed may be 0 even when per-VIN
  // fieldMapping records exist, because detailed changes live in
  // artifacts. Prefer artifact recordCount for this stage.
  const recordCount = summary?.artifacts?.recordCount
  if (typeof recordCount === 'number' && Number.isFinite(recordCount)) {
    return Math.max(0, Math.floor(recordCount))
  }

  const changed = summary?.counts?.changed
  if (typeof changed === 'number' && Number.isFinite(changed)) {
    return Math.max(0, Math.floor(changed))
  }

  return null
}

const businessSectionSummaryContext = computed<BusinessSectionSummaryContext>(() => {
  const visibility = visibilityTotals.value

  return {
    eligibilityCheck: {
      sourceVinCount: resolveEligibilitySourceVinCount(),
      eligibleForMappingCount: upstreamVehicleCount('fieldMapping'),
      participatingFeedCount: preProcessorParticipatingFeeds.value.length || null,
      // We do not compute a publish-wide deduped union (only per-feed unique
      // counts are available), so never claim uniqueness for multi-feed runs.
      sourceVinCountIsDeduped: false,
      // Multi-feed reconciliation inputs (derived from existing flow metrics +
      // the deduped pass-through artifact count that the View VINs modal totals).
      rawSourceTotal: preProcessorRawSourceTotal.value,
      uniquePassThroughCount: passThroughUniqueVinCount.value,
      feedDisplayName: preProcessorParticipatingFeeds.value.length > 1 ? null : preProcessorSourceLabel(),
    },
    inventorySync: {
      funnel: resolveInventorySyncFunnel(),
    },
    fieldMapping: {
      vehiclesChecked: resolvePublishCandidateMovementCount(),
      mappingDecisionsCaptured: resolveFieldMappingDecisionsCaptured(),
    },
    vehicleGroupings: {
      vehiclesEvaluated: resolvePublishCandidateMovementCount(),
      changedCount: stageSummaryChangedCount('groups'),
    },
    publishRulesEvaluation: {
      vehiclesEvaluated:
        typeof visibility?.evaluatedCount === 'number'
          ? visibility.evaluatedCount
          : (numericCountFromStructured(
              (
                stageSummaryFor('listingRules')?.details as
                  | PublishFlowObservabilityServiceDetails
                  | undefined
              )?.input,
            ) ?? null),
      accepted: typeof visibility?.acceptedCount === 'number' ? visibility.acceptedCount : null,
      blocked: typeof visibility?.blockedCount === 'number' ? visibility.blockedCount : null,
    },
  }
})

// Authoritative reconciled Inventory Sync funnel, bound directly by the panel
// so the template avoids optional-context access on the summary context.
const inventorySyncFunnel = computed<PublishFlowStageDetailFunnel | null>(() =>
  resolveInventorySyncFunnel(),
)
const inventorySyncHeldBackCount = computed(() =>
  Math.max(0, Number(inventorySyncFunnel.value?.heldBackCount ?? 0)),
)
type InventorySyncSectionPanelExpose = { openHeldBackDrillDown: () => void }
const inventorySyncSectionPanelRef = ref<
  InventorySyncSectionPanelExpose | InventorySyncSectionPanelExpose[] | null
>(null)

function openInventorySyncHeldBack() {
  const panel = Array.isArray(inventorySyncSectionPanelRef.value)
    ? inventorySyncSectionPanelRef.value[0]
    : inventorySyncSectionPanelRef.value
  panel?.openHeldBackDrillDown()
}

const publishFlowBusinessSections = computed<PublishFlowBusinessSectionViewModel[]>(() => {
  const rollupContext = {
    preProcessorMeasured: Boolean(preProcessorMetrics.value),
    visibilityMeasured: Boolean(visibilityTotals.value),
  }
  const cardsByKey = canonicalStageCardsByKey.value
  const summaryContext = businessSectionSummaryContext.value

  return PUBLISH_FLOW_BUSINESS_SECTIONS.map((section) => {
    const subStages = section.subStages
      .map((stageKey) => cardsByKey.get(stageKey))
      .filter((card): card is CanonicalStageCard => Boolean(card))
    const signals = subStages.map((card) => stageRollupSignal(card, rollupContext))
    const aggregated = aggregateBusinessSectionStatus(signals)
    // Feed Preparation pass-through reads as "No filters applied" rather than
    // the generic "Not needed" so dealers don't confuse it with Feed Source
    // Config (which is a separate, required publish gate).
    const statusLabel = compactBusinessSectionStatusLabel(section.id, summaryContext, aggregated.label)
    return {
      id: section.id,
      label: friendlyPublishFlowBusinessSectionLabel(section.id),
      summaryLine: buildBusinessSectionSummaryLine(section.id, summaryContext),
      handoffLine: buildBusinessSectionHandoffLine(section.id, summaryContext),
      statusLabel,
      statusColor: compactBusinessSectionStatusColor(statusLabel, aggregated.color),
      subStages,
      showExpandedDetails: businessSectionShowExpandedDetails(section.id, summaryContext),
    }
  })
})

function formatPublishStageHandoffCount(count: number): string {
  const n = Math.max(0, Math.floor(count))
  return n === 1 ? '1 vehicle' : `${n} vehicles`
}

function buildBusinessSectionHandoffLine(
  sectionId: PublishFlowBusinessSectionId,
  ctx: BusinessSectionSummaryContext,
): string | null {
  // For multi-feed runs, per-stage handoff counts are per-feed/slice values, not
  // publish-wide totals, so numeric bridges (e.g. "89 → 192 → 207") are
  // misleading. `participatingFeedCount` is only carried on the eligibility ctx.
  const participatingFeedCount = ctx.eligibilityCheck.participatingFeedCount
  const isMultiFeed =
    typeof participatingFeedCount === 'number' && participatingFeedCount > 1
  switch (sectionId) {
    case 'eligibilityCheck': {
      // Feed Preparation hands the prepared vehicles to Field Mapping (the first
      // stage that runs over the feed-changed upsert set during SRP creation).
      // Multi-feed runs: per-feed eligible counts are slices, so prefer the
      // deduped pass-through artifact count when it is available.
      if (isMultiFeed) {
        const unique = ctx.eligibilityCheck.uniquePassThroughCount
        if (typeof unique === 'number' && unique >= 0) {
          return `\u2192 ${formatPublishStageHandoffCount(unique)} moved to Field Mapping.`
        }
        return '\u2192 Vehicles moved to Field Mapping.'
      }
      const count = ctx.eligibilityCheck.eligibleForMappingCount
      return typeof count === 'number'
        ? `\u2192 ${formatPublishStageHandoffCount(count)} moved to Field Mapping.`
        : null
    }
    case 'fieldMapping': {
      // Field Mapping (and its nested Feed Field Rules) runs before Inventory
      // Sync. The feed-prepared candidate set flows into Inventory Sync next, so
      // prefer the authoritative Inventory Sync intake count — this matches the
      // Inventory Sync "{intake} feed-prepared VINs reconciled" headline and
      // never implies Field Mapping only processed the continued subset.
      const intake = finiteNumber(ctx.inventorySync?.funnel?.intakeCount)
      const count = intake ?? ctx.fieldMapping.vehiclesChecked
      return typeof count === 'number'
        ? `\u2192 ${formatPublishStageHandoffCount(count)} moved to Inventory Sync.`
        : null
    }
    case 'inventorySync': {
      const vm = buildInventorySyncCardViewModel(ctx.inventorySync?.funnel ?? null)
      return vm.hasFunnel ? `\u2192 ${vm.handoffLine}` : null
    }
    case 'vehicleGroupings': {
      const count = ctx.vehicleGroupings.vehiclesEvaluated
      return typeof count === 'number'
        ? `\u2192 ${formatPublishStageHandoffCount(count)} moved to Publish Rules Evaluation.`
        : null
    }
    case 'publishRulesEvaluation': {
      const count = ctx.publishRulesEvaluation.accepted
      if (typeof count !== 'number') return null
      return `\u2192 ${formatPublishStageHandoffCount(count)} moved to Website Publish & Refresh.`
    }
    default:
      return null
  }
}

function compactBusinessSectionStatusLabel(
  sectionId: PublishFlowBusinessSectionId,
  ctx: BusinessSectionSummaryContext,
  fallback: string,
): string {
  switch (sectionId) {
    case 'eligibilityCheck': {
      const { sourceVinCount, eligibleForMappingCount } = ctx.eligibilityCheck
      if (typeof sourceVinCount === 'number' && typeof eligibleForMappingCount === 'number') {
        return eligibleForMappingCount < sourceVinCount ? 'Filtered' : 'Checked'
      }
      return fallback === 'Not needed' ? 'Checked' : fallback
    }
    case 'inventorySync': {
      const funnel = ctx.inventorySync?.funnel ?? null
      if (!funnel) return fallback
      // "Filtered" when VINs were held back from the candidate set; otherwise
      // everything that passed Feed Preparation continued.
      return Math.max(0, Math.floor(funnel.heldBackCount)) > 0 ? 'Filtered' : 'Checked'
    }
    case 'fieldMapping': {
      const changed = ctx.fieldMapping.mappingDecisionsCaptured
      if (typeof changed === 'number') return changed > 0 ? 'Changed' : 'No changes'
      return fallback
    }
    case 'vehicleGroupings': {
      const changed = ctx.vehicleGroupings.changedCount
      if (typeof changed === 'number') return changed > 0 ? 'Changed' : 'No changes'
      return fallback
    }
    case 'publishRulesEvaluation': {
      const blocked = ctx.publishRulesEvaluation.blocked
      if (typeof blocked === 'number') return blocked > 0 ? 'Matched rules' : 'Passed'
      return fallback
    }
    default:
      return fallback
  }
}

function businessSectionShowExpandedDetails(
  sectionId: PublishFlowBusinessSectionId,
  ctx: BusinessSectionSummaryContext,
): boolean {
  if (sectionId === 'inventorySync') {
    // Only offer the funnel breakdown + drill-downs when a reconciled slice
    // was captured for this run.
    return (ctx.inventorySync?.funnel ?? null) !== null
  }
  if (sectionId === 'vehicleGroupings') {
    const changed = ctx.vehicleGroupings.changedCount
    const hasHandoff = typeof ctx.vehicleGroupings.vehiclesEvaluated === 'number'
    // Offer the expanded card whenever there is a group change to detail OR a
    // handoff count to surface (e.g. "N vehicles continued to Publish Rules
    // Evaluation"). The no-change case still adds value over the collapsed
    // summary via the handoff line. (Standing per-group distribution badges were
    // removed: segmentation only evaluates this run's processed subset, so those
    // counts could not represent the after-publish population.)
    return hasHandoff || (typeof changed === 'number' && changed > 0)
  }
  if (sectionId === 'publishRulesEvaluation') {
    const blocked = ctx.publishRulesEvaluation.blocked
    // Collapsed summary already states the all-passed outcome; expanded details add no value.
    return typeof blocked === 'number' && blocked > 0
  }
  return true
}

function compactBusinessSectionStatusColor(label: string, fallback: string): string {
  switch (label) {
    case 'Filtered':
    case 'Changed':
    case 'Matched rules':
      return 'warning'
    case 'No changes':
    case 'Passed':
    case 'Checked':
      return 'success'
    default:
      return fallback
  }
}

const expandedPublishStageSections = ref<Set<PublishStageSectionId>>(new Set())

function isPublishStageSectionExpanded(sectionId: PublishStageSectionId): boolean {
  return expandedPublishStageSections.value.has(sectionId)
}

function togglePublishStageSectionDetails(sectionId: PublishStageSectionId) {
  const next = new Set(expandedPublishStageSections.value)
  if (next.has(sectionId)) {
    next.delete(sectionId)
  } else {
    next.add(sectionId)
  }
  expandedPublishStageSections.value = next
}

function isGenericIoFallback(line: string | null | undefined): boolean {
  if (!line) return false
  return line.includes('Input/output details are not captured yet')
}

function normalizedStageExplanation(
  details: { explanation?: string } | undefined,
  fallback: StageDetailStatus,
  stageKey: PublishObservedStage,
): string {
  return details?.explanation?.trim() ?? fallbackStageExplanation(stageKey, fallback)
}

/** Second line beneath neutral IO fallback: status-based reason dealers care about. */
function stageOutcomeReason(status: StageDetailStatus, stageKey?: PublishObservedStage): string {
  switch (status) {
    case 'notConfigured':
      if (stageKey === 'feedFieldRules')
        return 'No feed field rules were configured for this publish run.'
      return 'No applicable configuration existed for this publish run.'
    case 'skipped':
      return 'This step was not needed for this publish run.'
    case 'empty':
      return 'Stage ran successfully and affected no vehicles.'
    case 'error':
      return 'This stage encountered an error during evaluation.'
    case 'unsupported':
    default:
      return 'Input/output details are not captured yet for this publish run.'
  }
}

function formatStageCountsLine(
  value: { label?: string; count?: number; description?: string } | undefined,
): string | null {
  if (!value) return null
  if (typeof value.description === 'string' && value.description.trim().length > 0) {
    return value.description.trim()
  }
  if (typeof value.count === 'number' && Number.isFinite(value.count)) {
    const n = Math.floor(value.count)
    const label =
      typeof value.label === 'string' && value.label.trim().length > 0 ? value.label : 'vehicles'
    return `${n} ${label}`
  }
  const label = typeof value.label === 'string' ? value.label.trim() : ''
  return label.length > 0 ? label : null
}

function formatPreProcessorInputDetail(): string | undefined {
  const sources = preProcessorSourceRowsFull.value
  if (sources.length === 1) {
    const src = sources[0]!
    const count =
      typeof src.vehicleCount === 'number' && Number.isFinite(src.vehicleCount)
        ? Math.max(0, Math.floor(src.vehicleCount))
        : 0
    return publishFlowPreProcessorCardInputCopy(formatPreProcessorSourceDisplayName(src), count)
  }
  if (sources.length > 1) {
    const total = preProcessorSourceVinTotal()
    if (typeof total === 'number') {
      const feedLabel = sources.length === 1 ? 'feed' : 'feeds'
      return `${sources.length} ${feedLabel} — ${total} VINs`
    }
    return `${sources.length} feeds`
  }
  const metrics = preProcessorMetrics.value
  const inputCount = getFlowPreProcessorInputCount(metrics)
  if (typeof inputCount === 'number' && Number.isFinite(inputCount)) {
    const count = Math.max(0, Math.floor(inputCount))
    const vins = count === 1 ? 'VIN' : 'VINs'
    return `${count} ${vins} from upstream feeds`
  }
  return undefined
}

function toggleRuleDetails(stage: PublishObservedStage): void {
  const next = new Set(expandedRuleDetailsStages.value)
  if (next.has(stage)) next.delete(stage)
  else next.add(stage)
  expandedRuleDetailsStages.value = next
}

function isRuleDetailsExpanded(stage: PublishObservedStage): boolean {
  return expandedRuleDetailsStages.value.has(stage)
}

function toggleStageMoreDetails(stage: PublishObservedStage): void {
  const next = new Set(expandedMoreDetailsStages.value)
  if (next.has(stage)) next.delete(stage)
  else next.add(stage)
  expandedMoreDetailsStages.value = next
}

function isStageMoreDetailsExpanded(stage: PublishObservedStage): boolean {
  return expandedMoreDetailsStages.value.has(stage)
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function preProcessorInputCount(
  metrics: FlowPreProcessor | null | undefined,
  stage: PublishStageSummary | null | undefined,
): number | null {
  return (
    finiteNumber(getFlowPreProcessorInputCount(metrics)) ??
    finiteNumber(stage?.details?.input?.count) ??
    finiteNumber(stage?.counts?.evaluated)
  )
}

function preProcessorOutputCount(
  metrics: FlowPreProcessor | null | undefined,
  stage: PublishStageSummary | null | undefined,
): number | null {
  return (
    finiteNumber(getFlowPreProcessorOutputCount(metrics)) ??
    finiteNumber(stage?.details?.output?.count) ??
    finiteNumber(stage?.counts?.unchanged) ??
    finiteNumber(stage?.counts?.changed)
  )
}

function preProcessorSkippedCount(
  metrics: FlowPreProcessor | null | undefined,
  stage: PublishStageSummary | null | undefined,
): number | null {
  const explicitSkipped = finiteNumber(stage?.counts?.skipped)
  if (explicitSkipped !== null) return explicitSkipped

  const input = preProcessorInputCount(metrics, stage)
  const output = preProcessorOutputCount(metrics, stage)

  if (input !== null && output !== null) {
    return Math.max(0, input - output)
  }

  return null
}

function buildPreProcessorOutcomeLines(
  metrics: FlowPreProcessor | null | undefined,
  stage: PublishStageSummary | null | undefined,
): string[] | undefined {
  const input = preProcessorInputCount(metrics, stage)
  const output = preProcessorOutputCount(metrics, stage)
  const skipped = preProcessorSkippedCount(metrics, stage)

  if (isFlowPreProcessorNotNeeded(metrics)) {
    if (output === null) return undefined
    return [metrics.explanation, `${output} source VINs passed through Feed Preparation.`]
  }

  const feed = preProcessorSourceLabel()
  const preProcessorOutcomeLines = [
    input !== null ? `${input} source VINs received from ${feed}.` : null,
    skipped !== null ? `${skipped} were filtered out.` : null,
    output !== null ? `${output} source VINs passed through Feed Preparation.` : null,
  ].filter((line): line is string => Boolean(line))

  return preProcessorOutcomeLines.length > 0 ? preProcessorOutcomeLines : undefined
}

function preProcessorSourceLabel(): string {
  const source = preProcessorSourceRowsFull.value[0]
  if (source) return formatPreProcessorSourceDisplayName(source)

  const feed = runSummary.value?.feedName
  return feed ? formatFeedDisplayName(feed) : 'feed'
}

function formatSourceVinCount(n: number): string {
  const safe = Math.max(0, Math.floor(n))
  return `${safe} ${safe === 1 ? 'source VIN' : 'source VINs'}`
}

function formatPreProcessorFeedSourceCountLine(fc: FlowFeedSourceCount): string {
  return `${formatFeedSource(fc.feed)}: ${formatSourceVinCount(fc.sourceVinCount)}`
}

function preProcessorNonFeedMoreDetails(lines: string[] | undefined): string[] {
  if (!Array.isArray(lines)) return []
  return lines.filter((line) => line.startsWith('In multiple feeds:'))
}

function buildPreProcessorMoreDetails(): string[] | undefined {
  const metrics = preProcessorMetrics.value

  // Multi-primary runs: show participating feeds with their unique per-feed
  // source VIN counts (derived from flow-metrics perFeed feed intake). These
  // are per-feed unique counts and may overlap across feeds.
  const feedSourceCounts = preProcessorFeedSourceCounts.value
  if (feedSourceCounts.length > 0) {
    const feedLines: string[] = []
    for (const fc of feedSourceCounts) {
      feedLines.push(`${formatFeedSource(fc.feed)}: ${formatSourceVinCount(fc.sourceVinCount)}`)
    }
    if (
      isFlowPreProcessorSummary(metrics) &&
      typeof metrics.multiFeedVinCount === 'number' &&
      metrics.multiFeedVinCount > 0
    ) {
      feedLines.push(`In multiple feeds: ${metrics.multiFeedVinCount} VINs`)
    }
    return feedLines
  }

  const details: string[] = []

  for (const src of preProcessorSourceRowsFull.value) {
    const feed = formatPreProcessorSourceDisplayName(src)
    const count =
      typeof src.vehicleCount === 'number' && Number.isFinite(src.vehicleCount)
        ? Math.max(0, Math.floor(src.vehicleCount))
        : null

    if (typeof count === 'number') {
      details.push(`${count} received from ${feed}`)
    } else {
      details.push(`Received from ${feed}`)
    }
  }

  if (metrics) {
    if (isFlowPreProcessorNotNeeded(metrics)) {
      // Pass-through count is already in the card summary line — omit here to
      // avoid repeating it when moreDetails are shown inline.
    } else {
      details.push(`${preProcessorDroppedCount.value} filtered out`)
      const continued = getFlowPreProcessorOutputCount(metrics)
      if (continued != null) {
        details.push(`${continued} source VINs passed through Feed Preparation`)
      }
    }
  }

  return details.length > 0 ? details : undefined
}

/**
 * Expanded Publish Eligibility card copy. The section compact row carries the
 * evaluated count; expanded details focus on the accepted/blocked outcome,
 * blocked classification, and readable rule breakdown.
 */
function buildListingRulesStageCardContent(
  totals: FlowVisibilityListingRules | null | undefined,
): Pick<
  CanonicalStageCard,
  | 'label'
  | 'summaryLine'
  | 'outcomeLines'
  | 'inputDetail'
  | 'resultDetail'
  | 'nextDetail'
  | 'moreDetails'
  | 'ruleResultLines'
  | 'detailButtonLabel'
> | null {
  if (!totals || typeof totals.evaluatedCount !== 'number') return null

  const evaluated = totals.evaluatedCount
  const blocked = totals.blockedCount ?? 0

  if (blocked === 0) {
    return {
      label: PUBLISH_ELIGIBILITY_LABEL,
      summaryLine: `All ${evaluated} evaluated vehicles passed publish rules.`,
      outcomeLines: ['No vehicles matched blocking rules.'],
    }
  }

  const accepted = totals.acceptedCount ?? Math.max(0, evaluated - blocked)

  return {
    label: PUBLISH_ELIGIBILITY_LABEL,
    summaryLine: `${accepted} accepted · ${blocked} blocked`,
    outcomeLines: buildBlockedClassificationLines(totals.blockedClassification),
    detailButtonLabel: 'View Matched Rules',
    ruleResultLines: buildPublishEligibilityRuleMatchLines(),
  }
}

/**
 * Phase 2 (additive): human-readable lines explaining whether blocked VINs were
 * newly blocked this run or already blocked beforehand. Returns [] when
 * classification is absent (legacy artifacts) so the existing blocked-count copy
 * renders unchanged.
 */
function buildBlockedClassificationLines(
  classification: FlowBlockedClassification | null | undefined,
): string[] {
  if (!classification) return []

  const newly = Math.max(0, Math.floor(classification.newlyBlockedThisRun ?? 0))
  const already = Math.max(0, Math.floor(classification.alreadyBlockedBeforeRun ?? 0))
  const unknown = Math.max(0, Math.floor(classification.unknownBlocked ?? 0))

  const lines: string[] = [
    newly === 1
      ? '1 newly blocked in this publish.'
      : `${newly} newly blocked in this publish.`,
    already === 1
      ? '1 was already blocked before this publish.'
      : `${already} were already blocked before this publish.`,
  ]
  if (unknown > 0) {
    lines.push(
      `${unknown} blocked ${
        unknown === 1 ? 'vehicle' : 'vehicles'
      } could not be classified from the available artifacts.`,
    )
  }
  return lines
}

function buildPublishEligibilityRuleMatchLines(): string[] | undefined {
  const totals = visibilityTotals.value
  return buildPublishEligibilityExpandedRuleMatchLines(
    visibilityRuleRows.value,
    totals?.blockedCount,
  )
}

/**
 * Numeric breakdown for the Publish Eligibility dialog header (unchanged behavior).
 */
function buildWebsiteVisibilityMoreDetails(): string[] {
  const totals = visibilityTotals.value
  const details: string[] = []

  if (totals) {
    details.push(`Vehicles evaluated: ${totals.evaluatedCount}`)
    details.push(`Accepted into inventory: ${totals.acceptedCount}`)
    details.push(`Matched blocking rules: ${totals.blockedCount}`)
  }

  return details
}

function numericCountFromStructured(structured: unknown): number | undefined {
  if (!structured || typeof structured !== 'object') return undefined
  const cand = structured as Record<string, unknown>
  const c = cand.count
  if (typeof c === 'number' && Number.isFinite(c)) return Math.floor(c)
  return undefined
}

type PublishFlowObservabilityServiceDetails = {
  status?: StageDetailStatus
  input?: { label?: string; count?: number; description?: string }
  output?: { label?: string; count?: number; description?: string }
  explanation?: string
  /** Present only for the `inventorySync` stage (reconciled funnel breakdown). */
  funnel?: PublishFlowStageDetailFunnel
}

/**
 * Resolve the reconciled Inventory Sync funnel slice from the manifest stage
 * details. This is the authoritative source for the Inventory Sync card; null
 * when the stage was not captured (older / non-reconciled runs).
 */
function resolveInventorySyncFunnel(): PublishFlowStageDetailFunnel | null {
  const details = stageSummaryFor('inventorySync')?.details as
    | PublishFlowObservabilityServiceDetails
    | undefined
  return details?.funnel ?? null
}

function upstreamVehicleCount(_forStage?: PublishObservedStage): number | null {
  const passed = preProcessorOutputCount(preProcessorMetrics.value, stageSummaryFor('preProcessor'))
  if (typeof passed === 'number') return passed
  return null
}

function stageSummaryChangedCount(stage: PublishObservedStage): number | null {
  if (stage === 'fieldMapping') {
    return resolveFieldMappingDecisionsCaptured()
  }

  const changed = stageSummaryFor(stage)?.counts?.changed
  if (typeof changed === 'number' && Number.isFinite(changed)) {
    return Math.max(0, Math.floor(changed))
  }

  return null
}

function resolveStageCardChipLabel(stage: PublishObservedStage, status: StageDetailStatus): string {
  if (stage === 'preProcessor') {
    if (isFlowPreProcessorNotNeeded(preProcessorMetrics.value)) {
      return FEED_PREPARATION_NO_FILTERS_BADGE
    }
    const manifestStage = stageSummaryFor('preProcessor')
    if (manifestStage?.availability === 'notApplicable' && manifestStage.outcome === 'skipped') {
      return FEED_PREPARATION_NO_FILTERS_BADGE
    }
    if (status === 'available') {
      return preProcessorMetrics.value ? 'Checked' : publishFlowStageChipLabel({ status })
    }
    return publishFlowStageChipLabel({ status })
  }

  if (stage === 'listingRules' && visibilityTotals.value) {
    // Phase 3 — Publish Rules Evaluation ran (flow-metrics measured it). Never
    // show the "Not needed"/skipped label, even when the per-VIN decision
    // artifact is absent (e.g. a run that only re-blocked already-blocked VINs,
    // whose drill-down is served by the blocked-decisions artifact instead).
    const measuredLabel = publishFlowStageChipLabel({
      status,
      changedCount: stageSummaryChangedCount(stage),
      hasDecisionRecords: hasDecisionPointers(stage),
    })
    return measuredLabel === 'Not needed' || measuredLabel === 'Not configured'
      ? 'Checked'
      : measuredLabel
  }

  return publishFlowStageChipLabel({
    status,
    changedCount: stageSummaryChangedCount(stage),
    hasDecisionRecords: hasDecisionPointers(stage),
  })
}

function observabilityExplanationIsInternalJargon(line: string): boolean {
  return (
    /\bvehicle-level publish decisions were captured\b/i.test(line) ||
    /\bvehicle decisions were captured\b/i.test(line) ||
    /\bvehicle group decisions were captured\b/i.test(line) ||
    /\blisting visibility decisions were captured\b/i.test(line) ||
    /\bfield mapping decisions were captured\b/i.test(line) ||
    /\bdetails were captured for this publish\b/i.test(line) ||
    (/\bfor this stage\.?\s*$/i.test(line) && /\bdecisions were captured\b/i.test(line))
  )
}

/**
 * Feed label to attribute a Field Mapping count to in multi-feed runs. The
 * publish-wide mapping count reflects a single feed (the last writer), so we
 * attribute it to the feed whose source VIN count matches; otherwise fall back
 * to the run's primary feed. Returns null when no feed label can be resolved.
 */
function fieldMappingAttributionFeedLabel(count: number | null): string | null {
  const feeds = preProcessorFeedSourceCounts.value
  if (typeof count === 'number') {
    const match = feeds.find((fc) => fc.sourceVinCount === count)
    if (match) return formatFeedSource(match.feed)
  }
  const feed = runSummary.value?.feedName
  return feed ? formatFeedSource(feed) : null
}

function mergeFieldMappingStageSections(
  details: PublishFlowObservabilityServiceDetails | undefined,
  lineInFormatted: string | null,
  lineOutFormatted: string | null,
  observabilityExplanation: string,
): Pick<CanonicalStageCard, 'summaryLine' | 'outcomeLines'> {
  const nIn = numericCountFromStructured(details?.input)
  const changed = resolveFieldMappingDecisionsCaptured()
  // Field Mapping runs before Inventory Sync, so it hands the feed-prepared
  // candidate set to Inventory Sync next. Prefer the authoritative Inventory
  // Sync intake (feed-prepared VINs reconciled) so the count matches the
  // Inventory Sync headline. Fall back to the publish-wide candidate movement
  // count when no reconciled funnel was captured — this stays publish-wide
  // (never a misleading per-feed slice) for multi-feed runs.
  const inventorySyncIntakeCount = finiteNumber(resolveInventorySyncFunnel()?.intakeCount)
  const inputCount =
    inventorySyncIntakeCount ?? resolvePublishCandidateMovementCount() ?? nIn ?? null
  const nextStage = 'Inventory Sync'

  const mappingDecisionsCaptured = changed

  let summaryLine = 'Field mapping evaluated vehicle data.'
  if (typeof mappingDecisionsCaptured === 'number' && mappingDecisionsCaptured > 0) {
    const noun = mappingDecisionsCaptured === 1 ? 'mapping decision' : 'mapping decisions'
    summaryLine = `${mappingDecisionsCaptured} ${noun} recorded.`
  } else {
    const expl = observabilityExplanation.trim()
    if (expl.length > 0 && !observabilityExplanationIsInternalJargon(expl)) {
      summaryLine = expl.endsWith('.') ? expl : `${expl}.`
    }
  }

  const outcomeLines: string[] = []
  const continuedCount = typeof inputCount === 'number' ? inputCount : undefined
  if (typeof continuedCount === 'number') {
    outcomeLines.push(`${formatPublishStageHandoffCount(continuedCount)} continued to ${nextStage}.`)
  } else if (lineOutFormatted && !isGenericIoFallback(lineOutFormatted)) {
    const cleaned = lineOutFormatted.replace(/\bvehicle decisions?\b/gi, 'mapping outcomes').trim()
    if (cleaned.length > 0) outcomeLines.push(cleaned)
  }

  // Multi-feed runs: the publish-wide count reflects a single feed, so attribute
  // it to that feed rather than implying it is the publish-wide vehicle total,
  // and drop the per-feed number from the input/continuation lines (the real
  // publish-wide total lives downstream in Publish Eligibility).
  if (preProcessorFeedSourceCounts.value.length > 1) {
    const feedLabel = fieldMappingAttributionFeedLabel(inputCount)
    const subject = feedLabel ? `${feedLabel} feed vehicles` : 'feed vehicles'
    const multiFeedSummary =
      typeof mappingDecisionsCaptured === 'number' && mappingDecisionsCaptured > 0
        ? `Mapped ${subject} for this publish; ${mappingDecisionsCaptured} ${
            mappingDecisionsCaptured === 1 ? 'mapping decision was' : 'mapping decisions were'
          } recorded.`
        : `Mapped ${subject} for this publish; no field values changed.`
    return {
      summaryLine: multiFeedSummary,
      outcomeLines,
    }
  }

  return {
    summaryLine,
    outcomeLines,
  }
}

function stageSummaryFor(stage: PublishObservedStage) {
  return currentSummary.value?.stages?.[stage] ?? currentManifest.value?.stages?.[stage]
}

function canDrillDownStage(stage: PublishStageSummary | null | undefined): boolean {
  if (!stage) return false
  if (
    stage.stage === 'preProcessor' &&
    (stage.availability === 'notApplicable' ||
      stage.outcome === 'skipped' ||
      stage.artifacts?.status === 'notRequired' ||
      stage.details?.status === 'skipped')
  ) {
    return false
  }

  return (
    stage.availability === 'available' &&
    stage.artifacts?.status === 'available' &&
    Number(stage.artifacts?.recordCount ?? 0) > 0
  )
}

function stageDetailStatusFor(stage: PublishObservedStage): StageDetailStatus {
  const summary = stageSummaryFor(stage)
  const details = summary?.details as PublishFlowObservabilityServiceDetails | undefined
  return details?.status ?? availabilityToStageStatus(summary?.availability)
}

function buildFeedFieldRulesSubsection(): FeedFieldRulesSubsectionViewModel | undefined {
  const summary = stageSummaryFor('feedFieldRules')
  const hasDecisionRecords = hasDecisionPointers('feedFieldRules')
  if (!summary && !hasDecisionRecords) return undefined

  const status = stageDetailStatusFor('feedFieldRules')
  const changedCount = summary?.counts?.changed ?? 0
  return {
    summaryLine: publishFlowFeedFieldRulesSubsectionSummaryCopy({ status, changedCount }),
    hasDetails: hasDecisionRecords,
    status,
    changedCount,
  }
}

function publishFlowStageHelpText(stage: PublishObservedStage): string | undefined {
  if (stage === 'preProcessor') {
    return 'Evaluates feed vehicles against Preprocessor filters. Filtered vehicles are removed before the feed continues through publish.'
  }

  return undefined
}

/**
 * Manifest-driven availability of the Feed Preparation VIN drilldown. The
 * button shows whenever the publish manifest carries either real preProcessor
 * decision pointers (filtered runs) OR a pass-through artifact (direct-account
 * / no-filter runs where every source VIN passed Feed Preparation unchanged).
 * Intentionally independent of feed count, dropped count, filters-applied, or
 * expanded/collapsed state — those never gate the drilldown.
 */
function hasPreProcessorVinDrillDown(): boolean {
  return (
    hasDecisionPointers('preProcessor') || hasPreProcessorPassThroughPointer(currentManifest.value)
  )
}

/**
 * Drill-down button label per stage. Pre-processor surfaces VINs that
 * passed Feed Preparation ("View VINs"); Field Mapping surfaces per-field
 * mapping decisions ("View decisions"); Vehicle Groupings surfaces VIN-level
 * group membership changes ("View Group Changes"); every other stage keeps the
 * generic "View details" default applied in the template.
 */
function resolveStageDetailButtonLabel(stage: PublishObservedStage): string | undefined {
  if (stage === 'preProcessor') return 'View VINs'
  if (stage === 'fieldMapping') return 'View decisions'
  // Vehicle Groupings only exposes a drill-down when group memberships changed,
  // so the action inspects those changes rather than offering a general lookup.
  if (stage === 'groups') return 'View Group Changes'
  return undefined
}

function buildCanonicalStageCard(stage: PublishObservedStage): CanonicalStageCard {
  const summary = stageSummaryFor(stage)
  const details = summary?.details as PublishFlowObservabilityServiceDetails | undefined
  const status = details?.status ?? availabilityToStageStatus(summary?.availability)
  const observabilityExplanation = normalizedStageExplanation(details, status, stage)
  const lineInFormatted = formatStageCountsLine(details?.input)
  const lineOutFormatted = formatStageCountsLine(details?.output)
  const hasDupFallback =
    isGenericIoFallback(lineInFormatted) && isGenericIoFallback(lineOutFormatted)

  const base: Omit<CanonicalStageCard, 'summaryLine' | 'feedFieldRulesSubsection'> = {
    stage,
    label: stage === 'preProcessor' ? 'Pre-processor' : friendlyPublishFlowStageLabel(stage),
    helpText: publishFlowStageHelpText(stage),
    statusLabel: resolveStageCardChipLabel(stage, status),
    statusColor: publishFlowStageChipColor(resolveStageCardChipLabel(stage, status), status),
    detailStatus: status,
    hasDetails:
      stage === 'preProcessor'
        ? hasPreProcessorVinDrillDown()
        : canDrillDownStage(summary) && hasDecisionPointers(stage),
    detailButtonLabel: resolveStageDetailButtonLabel(stage),
    observabilityExplanation,
  }

  if (isStageMissingBecausePublishFailed(stage)) {
    return {
      ...base,
      statusLabel: 'Not run',
      statusColor: 'grey',
      detailStatus: 'skipped',
      hasDetails: false,
      summaryLine: 'Not run — publish failed before this stage.',
      resultDetail: 'Publish failed before this stage could complete.',
    }
  }

  if (stage === 'preProcessor') {
    const metrics = preProcessorMetrics.value
    const manifestStage = summary
    const output = preProcessorOutputCount(metrics, manifestStage)

    const passThroughCount = currentManifest.value?.preProcessorPassThrough?.recordCount ?? null
    const movedCount = output ?? passThroughCount
    const passThroughClickable = hasPreProcessorVinDrillDown()

    if (isFlowPreProcessorNotNeeded(metrics)) {
      return {
        ...base,
        statusLabel: FEED_PREPARATION_NO_FILTERS_BADGE,
        statusColor: publishFlowStageChipColor(FEED_PREPARATION_NO_FILTERS_BADGE, 'skipped'),
        detailStatus: 'skipped',
        hasDetails: passThroughClickable,
        summaryLine: buildNoFiltersSummaryLine(),
        moreDetails: buildPreProcessorMoreDetails(),
      }
    }

    if (manifestStage?.availability === 'notApplicable' && manifestStage.outcome === 'skipped') {
      // Multi-feed runs: the publish-wide pass-through count reflects only the
      // last feed written, so never present it as a single headline. Defer to
      // the feed-aware no-filters copy and let the per-feed breakdown carry the
      // real counts. Single-feed runs keep the precise pass-through count.
      const movedDirectlyLine =
        preProcessorFeedSourceCounts.value.length > 1
          ? buildNoFiltersSummaryLine()
          : movedCount !== null
            ? `${movedCount} source VINs passed through Feed Preparation. No Pre-processor filters were configured for this run.`
            : null
      return {
        ...base,
        statusLabel: FEED_PREPARATION_NO_FILTERS_BADGE,
        statusColor: publishFlowStageChipColor(FEED_PREPARATION_NO_FILTERS_BADGE, 'skipped'),
        detailStatus: 'skipped',
        hasDetails: passThroughClickable,
        summaryLine:
          movedDirectlyLine ?? manifestStage.details?.explanation ?? base.observabilityExplanation,
        moreDetails: buildPreProcessorMoreDetails(),
      }
    }

    if (output !== null) {
      return {
        ...base,
        summaryLine: '',
        outcomeLines: buildPreProcessorOutcomeLines(metrics, manifestStage),
      }
    }

    const proc = observabilityExplanation.trim()
    return {
      ...base,
      summaryLine:
        proc.length > 0 ? proc : 'Feed preparation details are not captured yet for this publish.',
      outcomeLines: buildPreProcessorOutcomeLines(metrics, manifestStage),
    }
  }

  if (stage === 'fieldMapping') {
    const feedFieldRulesSubsection = buildFeedFieldRulesSubsection()
    const mergedFromMetrics = mergeFieldMappingStageSections(
      details,
      lineInFormatted,
      lineOutFormatted,
      observabilityExplanation,
    )

    if (hasDupFallback) {
      return {
        ...base,
        ...mergedFromMetrics,
        feedFieldRulesSubsection,
      }
    }

    return {
      ...base,
      ...mergedFromMetrics,
      statusLabel: resolveStageCardChipLabel('fieldMapping', status),
      statusColor: publishFlowStageChipColor(
        resolveStageCardChipLabel('fieldMapping', status),
        status,
      ),
      feedFieldRulesSubsection,
    }
  }

  if (stage === 'groups') {
    if (hasDupFallback) {
      return {
        ...base,
        summaryLine: stageOutcomeReason(status, stage),
        resultDetail: STAGE_FALLBACK_IO_LINE,
      }
    }

    const changedCount = stageSummaryChangedCount('groups')
    const candidateCount = resolvePublishCandidateMovementCount()
    const movementLine =
      typeof candidateCount === 'number'
        ? `${formatPublishStageHandoffCount(candidateCount)} continued to Publish Rules Evaluation.`
        : undefined
    if (changedCount === 0) {
      return {
        ...base,
        label: 'Vehicle Groupings',
        summaryLine: '',
        // No VIN drill-down when nothing changed groups — a "View groups" VIN
        // lookup is not a publish-flow action. The expanded details card
        // surfaces the standing group distribution instead.
        hasDetails: false,
        detailButtonLabel: undefined,
        outcomeLines: [
          publishFlowGroupsChangedResultCopy(0),
          ...(movementLine ? [movementLine] : []),
        ],
      }
    }

    const resultDetail = publishFlowGroupsChangedResultCopy(changedCount)

    return {
      ...base,
      label: 'Vehicle Groupings',
      summaryLine: '',
      // Group memberships changed this run, so the drill-down inspects those
      // specific VIN-level changes rather than offering a general lookup.
      detailButtonLabel: 'View Group Changes',
      outcomeLines: [
        resultDetail,
        ...(movementLine ? [movementLine] : []),
      ],
    }
  }

  if (stage === 'listingRules') {
    const totals = visibilityTotals.value
    const compact = buildListingRulesStageCardContent(totals)
    const listingRulesButtonLabel =
      typeof totals?.blockedCount === 'number' && totals.blockedCount > 0
        ? 'View Matched Rules'
        : undefined
    if (compact) {
      return {
        ...base,
        ...compact,
        detailButtonLabel: listingRulesButtonLabel,
      }
    }

    const evaluated = totals?.evaluatedCount

    if (hasDupFallback) {
      return {
        ...base,
        label: PUBLISH_ELIGIBILITY_LABEL,
        summaryLine: stageOutcomeReason(status, stage),
        resultDetail: STAGE_FALLBACK_IO_LINE,
        detailButtonLabel: listingRulesButtonLabel,
      }
    }

    let summaryLine = 'Publish eligibility details are not captured yet.'
    if (typeof evaluated === 'number') {
      summaryLine = `${evaluated} vehicles were evaluated for inventory membership.`
    } else {
      const explTrim = observabilityExplanation.trim()
      if (explTrim.length > 0 && !observabilityExplanationIsInternalJargon(explTrim)) {
        summaryLine = explTrim
      }
    }

    return {
      ...base,
      label: PUBLISH_ELIGIBILITY_LABEL,
      summaryLine,
      detailButtonLabel: listingRulesButtonLabel,
    }
  }

  if (hasDupFallback) {
    return {
      ...base,
      summaryLine: stageOutcomeReason(status, stage),
      resultDetail: STAGE_FALLBACK_IO_LINE,
    }
  }

  const resolvedIn = lineInFormatted && !isGenericIoFallback(lineInFormatted)
  const resolvedOut = lineOutFormatted && !isGenericIoFallback(lineOutFormatted)
  const explTrim = observabilityExplanation.trim()
  const defaultResult =
    explTrim.length > 0 && !observabilityExplanationIsInternalJargon(explTrim)
      ? explTrim
      : STAGE_FALLBACK_IO_LINE

  return {
    ...base,
    summaryLine: defaultResult,
    inputDetail: resolvedIn ? (lineInFormatted as string) : undefined,
    nextDetail: resolvedOut ? (lineOutFormatted as string) : undefined,
    resultDetail: defaultResult,
  }
}

function availabilityToStageStatus(availability?: string): StageDetailStatus {
  if (availability === 'available') return 'available'
  if (availability === 'notApplicable') return 'skipped'
  if (availability === 'notConfigured') return 'notConfigured'
  if (availability === 'unsupported') return 'unsupported'
  return 'unsupported'
}

function formatStageDetailValue(value?: {
  label?: string
  count?: number
  description?: string
}): string | null {
  if (!value) return null
  if (value.description) return value.description
  if (typeof value.count === 'number')
    return `${value.count} ${value.label?.toLowerCase() ?? 'vehicles'}`
  return value.label ?? null
}

function stageStatusLabel(status: StageDetailStatus): string {
  return publishFlowStageChipLabel({ status })
}

function stageStatusColor(status: StageDetailStatus): string {
  if (status === 'available') return 'success'
  if (status === 'empty') return 'info'
  if (status === 'error') return 'error'
  return 'grey'
}

function hasDecisionPointers(stage: PublishObservedStage): boolean {
  const pointers = currentManifest.value?.stageDecisions?.[stage]
  return Array.isArray(pointers) && pointers.length > 0
}

/**
 * Feed Preparation pass-through availability (direct-account / no-preProcessor
 * runs). Drives Feed Preparation card click-ability when no real preProcessor
 * decision artifacts exist. NOT a new visible stage.
 */
function hasPreProcessorPassThroughPointer(
  manifest: { preProcessorPassThrough?: { parts?: unknown } } | null | undefined,
): boolean {
  const parts = manifest?.preProcessorPassThrough?.parts
  return Array.isArray(parts) && parts.length > 0
}

function fallbackStageInput(_stage: PublishObservedStage): string {
  return 'Input/output details are not captured yet for this stage.'
}

function fallbackStageOutput(_stage: PublishObservedStage, status: StageDetailStatus): string {
  if (status === 'empty') return 'No vehicles were affected.'
  return 'Input/output details are not captured yet for this stage.'
}

function fallbackStageExplanation(stage: PublishObservedStage, status: StageDetailStatus): string {
  if (status === 'unsupported') return 'Input/output details are not captured yet for this stage.'
  if (status === 'notConfigured') return 'No applicable configuration existed for this publish run.'
  if (status === 'skipped') return 'This step was not needed for this publish run.'
  if (status === 'empty') return 'Stage ran successfully and affected no vehicles.'
  return `${friendlyPublishFlowStageLabel(stage)} details were captured for this publish.`
}

/**
 * Phase 6.y + 7.y + 8.y + 10.3.y + 10.5.y gate. The dialog must only
 * mount when:
 *   1. We have a manifest (Phase 5.x observability fetch resolved ok).
 *   2. The manifest carries at least one decision pointer for ANY
 *      browser-supported decision stage (`listingRules`,
 *      `preProcessor`, `feedFieldRules`,
 *      `fieldMapping`, `groups`, `listingRules`). The OR is
 *      normalized across the entire allow-list
 *      so every instrumented stage produces the same dialog
 *      affordance — there is never a manifest where the writer
 *      produced decision artifacts but the dealer cannot open them.
 *   3. We have account + session credentials to call the authenticated
 *      decision-read endpoints from inside the dialog.
 *
 * Without all three, the button is hidden by the card's own gate AND
 * the dialog instance is never created, so a stale click can't
 * resurrect it.
 *
 * The full set of browser-supported stages is enumerated locally
 * (rather than imported from the store) so the gate's intent stays
 * obvious at the call site and a future stage forces a code-review
 * change in this file. Defensive on missing `stageDecisions` and
 * non-array pointer collections on any stage.
 *
 * Note on the listingRules cursor asymmetry: this is a backend
 * limitation in the legacy listingRules cursor (Phase 6.x predates
 * the cross-stage cursor stamp); the frontend gate is intentionally
 * symmetric across all instrumented stages.
 */
const DETAILED_IMPACT_BROWSER_STAGES = [
  'preProcessor',
  'feedFieldRules',
  'fieldMapping',
  'groups',
  'listingRules',
] as const

function manifestHasAnyDecisionPointer(
  manifest: { stageDecisions?: Record<string, unknown> } | null | undefined,
): boolean {
  if (!manifest) return false
  const decisions = manifest.stageDecisions ?? {}
  for (const stage of DETAILED_IMPACT_BROWSER_STAGES) {
    const pointers = decisions[stage]
    if (Array.isArray(pointers) && pointers.length > 0) return true
  }
  return false
}

const detailedImpactReady = computed(() => {
  const manifest = currentManifest.value
  if (!manifestHasAnyDecisionPointer(manifest) && !hasPreProcessorPassThroughPointer(manifest)) {
    return false
  }
  return Boolean(accountStore.accountId) && Boolean(accountStore.sessionToken)
})

function closeAllStageDetails() {
  detailedPublishImpactStore.closeAllStageDetails()
}

function isDetailedImpactBrowserStage(
  stage: PublishObservedStage,
): stage is (typeof DETAILED_IMPACT_BROWSER_STAGES)[number] {
  return (DETAILED_IMPACT_BROWSER_STAGES as readonly string[]).includes(stage)
}

function onOpenDetailedImpactForStage(stage: PublishObservedStage) {
  if (!detailedImpactReady.value) return
  const manifest = currentManifest.value
  if (!manifest) return

  // Vehicle Groupings has no VIN drill-down when nothing changed groups. The
  // main UI hides the trigger in that case; this guards a direct/programmatic
  // call so the Vehicle Groups modal never opens for the no-change run. Mirrors
  // the card's button-hiding rule, which only suppresses on an explicit zero.
  if (stage === 'groups' && stageSummaryChangedCount('groups') === 0) {
    return
  }

  if (
    detailedPublishImpactStore.dialogOpen &&
    detailedPublishImpactStore.browserStageScope === stage
  ) {
    return
  }

  closeAllStageDetails()

  if (stage === 'preProcessor' && hasDecisionPointers('preProcessor')) {
    detailedPublishImpactStore.openDialog(manifest, { stage: 'preProcessor' })
    return
  }

  // Direct-account / no-preProcessor run: open the Feed Preparation
  // pass-through view (VINs that passed Feed Preparation unchanged).
  if (stage === 'preProcessor' && hasPreProcessorPassThroughPointer(manifest)) {
    detailedPublishImpactStore.openDialog(manifest, { mode: 'passThrough' })
    return
  }

  if (isDetailedImpactBrowserStage(stage)) {
    detailedPublishImpactStore.openDialog(manifest, { stage })
    return
  }

  detailedPublishImpactStore.openDialog(manifest)
}

const errors = computed(() => result.value?.errors ?? {})

const hasAnyData = computed(() => {
  if (!result.value) return false
  return Boolean(
    result.value.job ||
      result.value.artifacts ||
      result.value.logs ||
      result.value.runMetrics ||
      result.value.flowMetrics?.metrics,
  )
})

/** Stage-by-stage measurements (Phase 2). May be `null` for older runs. */
const flowMetrics = computed<FlowMetricsArtifact | null>(
  () => result.value?.flowMetrics?.metrics ?? null,
)
const feedIntakeRows = computed(() => buildFeedIntakeRows(flowMetrics.value))
const visibilityRuleRows = computed(() => buildVisibilityRuleRows(flowMetrics.value))
const visibilityTotals = computed(
  () => flowMetrics.value?.publishWide.visibilityListingRules ?? null,
)

/**
 * Authoritative publish-rule totals handed to the detailed-impact dialog so its
 * listingRules summary/notices agree with the page + header (and never falsely
 * report "no blocked" when per-VIN decision rows under-report blocked VINs).
 */
const detailedPublishAuthoritativeVisibility = computed(() => {
  const t = visibilityTotals.value
  if (!t) return null
  return {
    evaluated: typeof t.evaluatedCount === 'number' ? t.evaluatedCount : null,
    accepted: typeof t.acceptedCount === 'number' ? t.acceptedCount : null,
    blocked: typeof t.blockedCount === 'number' ? t.blockedCount : null,
  }
})
const segmentationTotals = computed(() => flowMetrics.value?.publishWide.segmentation ?? null)

const preProcessorMetrics = computed(() => flowMetrics.value?.publishWide.preProcessor ?? null)
const preProcessorDroppedCount = computed(() =>
  getFlowPreProcessorDroppedCount(preProcessorMetrics.value),
)

/** Badge text shown when Feed Preparation ran no filtering (pass-through). */
const FEED_PREPARATION_NO_FILTERS_BADGE = 'No filters applied'

/** Per-feed unique source VIN counts for multi-primary publishes. */
const preProcessorFeedSourceCounts = computed<FlowFeedSourceCount[]>(() =>
  getFlowPreProcessorFeedSourceCounts(preProcessorMetrics.value),
)
const preProcessorParticipatingFeeds = computed<string[]>(() =>
  getFlowPreProcessorParticipatingFeeds(preProcessorMetrics.value),
)

/**
 * Multi-feed count reconciliation (UI-only). For multi-feed publishes the
 * per-feed source counts (which may overlap) do not sum to the number of unique
 * VINs evaluated by Publish Rules. We derive both sides from existing flow
 * metrics so a user can reconcile "192 + 89 = 296 raw" against "226 unique":
 *   - raw total      = sum of per-feed `sourceVinCount`
 *   - unique evaluated = `publishWide.visibilityListingRules.evaluatedCount`
 * No artifact change is required; both fields already exist.
 */
const preProcessorRawSourceTotal = computed<number | null>(() => {
  const feeds = preProcessorFeedSourceCounts.value
  if (feeds.length < 2) return null
  let total = 0
  let any = false
  for (const fc of feeds) {
    if (typeof fc.sourceVinCount === 'number' && fc.sourceVinCount >= 0) {
      total += Math.floor(fc.sourceVinCount)
      any = true
    }
  }
  return any ? total : null
})

/**
 * Deduped unique VINs that passed Feed Preparation, from the canonical
 * pass-through artifact record count. This is the SAME number the View VINs
 * modal totals to, so the Feed Preparation card and the modal always reconcile.
 * Publish Rules Evaluation carries its own downstream evaluated count.
 */
const passThroughUniqueVinCount = computed<number | null>(() => {
  const count = currentManifest.value?.preProcessorPassThrough?.recordCount
  return typeof count === 'number' && Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null
})

/**
 * Feed Preparation reconciliation line (multi-feed only). Reconciles the THREE
 * counts the stage must never conflate: raw per-feed appearances (overlap),
 * deduped unique VINs that passed Feed Preparation, and — pointedly NOT — any
 * downstream evaluated count. Falls back to a non-numeric overlap note when the
 * unique pass-through count is unavailable.
 */
const multiFeedCountReconciliationLine = computed<string | null>(() => {
  if (preProcessorFeedSourceCounts.value.length < 2) return null
  const raw = preProcessorRawSourceTotal.value
  const unique = passThroughUniqueVinCount.value
  const feedCount = preProcessorFeedSourceCounts.value.length
  if (typeof unique !== 'number') {
    return 'Per-feed source VIN counts overlap across feeds, so they do not sum to the unique total.'
  }
  const uniquePhrase = `${unique} unique ${unique === 1 ? 'VIN' : 'VINs'} passed Feed Preparation`
  if (typeof raw !== 'number') {
    return `Per-feed source VIN counts overlap across feeds; ${uniquePhrase}.`
  }
  return `Per-feed source VIN counts overlap across ${feedCount} feeds: ${raw} raw appearances represented ${uniquePhrase}.`
})

/**
 * Body copy for a Feed Preparation pass-through (no filters) run. Uses
 * "source VINs" (feed input language) and never claims a deduped publish-wide
 * unique total for multi-feed runs (we only have per-feed unique counts).
 */
function buildNoFiltersSummaryLine(): string {
  const feedCount = preProcessorParticipatingFeeds.value.length
  if (feedCount > 1) {
    return 'No Pre-processor filters were configured for this run.'
  }
  const output = getFlowPreProcessorOutputCount(preProcessorMetrics.value)
  if (typeof output === 'number' && Number.isFinite(output)) {
    const n = Math.max(0, Math.floor(output))
    const vins = n === 1 ? 'source VIN' : 'source VINs'
    return `${n} ${vins} passed through Feed Preparation. No Pre-processor filters were configured for this run.`
  }
  return 'No Pre-processor filters were configured for this run.'
}

const PRE_PROCESSOR_FEEDS_PREVIEW = 3
const showAllPreProcessorFeeds = ref(false)
const expandedRuleDetailsStages = ref<Set<string>>(new Set())
const expandedMoreDetailsStages = ref<Set<string>>(new Set())

const preProcessorSourceRowsFull = computed<PreProcessorSourceRow[]>(() => {
  const metrics = preProcessorMetrics.value
  if (!metrics) return []
  const rows = isFlowPreProcessorSummary(metrics)
    ? metrics.perSourceCounts
    : metrics.perSourceCounts
  if (!Array.isArray(rows) || rows.length === 0) return []
  return rows
})

const preProcessorSourcesNeedCollapse = computed(
  () => preProcessorSourceRowsFull.value.length > PRE_PROCESSOR_FEEDS_PREVIEW,
)

const preProcessorInputsVisibleSources = computed(() => {
  const all = preProcessorSourceRowsFull.value
  if (all.length === 0) return []
  if (showAllPreProcessorFeeds.value || all.length <= PRE_PROCESSOR_FEEDS_PREVIEW) return all
  return all.slice(0, PRE_PROCESSOR_FEEDS_PREVIEW)
})

const detailedPublishStageDetailLines = computed<string[]>(() => {
  const stage = detailedPublishImpactStore.browserStageScope

  if (stage === 'preProcessor') {
    return buildPreProcessorMoreDetails() ?? []
  }

  if (stage === 'listingRules') {
    return buildWebsiteVisibilityMoreDetails() ?? []
  }

  return []
})

const detailedPublishStageDetailActions = computed<DetailedPublishStageAction[]>(() => {
  const stage = detailedPublishImpactStore.browserStageScope
  if (stage !== 'preProcessor') return []

  const actions: DetailedPublishStageAction[] = []

  preProcessorInputsVisibleSources.value.forEach((src, idx) => {
    const pointer = preProcessorSourceFilePointer(src)
    if (!pointer) return

    const cacheKey = publishFlowFileCacheKey(pointer, 'sourceInput')
    const unavailable = unavailablePublishFlowFiles.value.has(cacheKey)

    actions.push({
      id: `preProcessorSource:${idx}`,
      label:
        preProcessorInputsVisibleSources.value.length > 1
          ? `Download source file ${idx + 1}`
          : 'Download source file',
      icon: 'mdi-download',
      loading: downloadingPublishFlowFileKey.value === cacheKey,
      disabled: unavailable,
    })
  })

  if (preProcessorOutputFilePointer.value) {
    const cacheKey = publishFlowFileCacheKey(
      preProcessorOutputFilePointer.value,
      'preProcessorOutput',
    )
    const unavailable = unavailablePublishFlowFiles.value.has(cacheKey)

    actions.push({
      id: 'preProcessorOutput',
      label: 'Download prepared feed',
      icon: 'mdi-download',
      loading: downloadingPublishFlowFileKey.value === cacheKey,
      disabled: unavailable,
    })
  }

  return actions
})

const preProcessorOutputFilePointer = computed(() =>
  resolvePreProcessorOutputFilePointer({
    preProcessorOutputFile: preProcessorMetrics.value?.outputFile ?? null,
    runSummaryFileKey: runSummary.value?.fileKey ?? null,
    accountJazelId: accountStore.accountId,
  }),
)

const preProcessorOutputFileDownloadable = computed(() => {
  const pointer = preProcessorOutputFilePointer.value
  if (!pointer) return false
  return !unavailablePublishFlowFiles.value.has(
    publishFlowFileCacheKey(pointer, 'preProcessorOutput'),
  )
})

function preProcessorSourceFileDownloadable(row: PreProcessorSourceRow): boolean {
  const pointer = preProcessorSourceFilePointer(row)
  if (!pointer) return false
  return !unavailablePublishFlowFiles.value.has(publishFlowFileCacheKey(pointer, 'sourceInput'))
}

function preProcessorFeedSourceFileDownloadable(row: FlowFeedSourceCount): boolean {
  const pointer = preProcessorSourceFilePointer(row)
  if (!pointer) return false
  return !unavailablePublishFlowFiles.value.has(publishFlowFileCacheKey(pointer, 'sourceInput'))
}

function isPreProcessorFeedSourceFileDownloading(row: FlowFeedSourceCount): boolean {
  const pointer = preProcessorSourceFilePointer(row)
  if (!pointer) return false
  return downloadingPublishFlowFileKey.value === publishFlowFileCacheKey(pointer, 'sourceInput')
}

watch(preProcessorSourceRowsFull, (rows) => {
  if (rows.length <= PRE_PROCESSOR_FEEDS_PREVIEW) showAllPreProcessorFeeds.value = false
})

function formatPreProcessorSourceDisplayName(src: PreProcessorSourceRow): string {
  const feed = typeof src.sourceFeed === 'string' ? src.sourceFeed.trim() : ''
  if (feed.length > 0) return formatFeedDisplayName(feed)
  return typeof src.sourceJuid === 'string' && src.sourceJuid.length > 0
    ? src.sourceJuid
    : 'Unknown source'
}

function formatFeedDisplayName(feed: string): string {
  const normalized = String(feed || '').trim()
  if (normalized.toLowerCase() === 'vauto') return 'vAuto'
  return normalized
}

const runSummary = computed<RunSummaryViewModel | null>(() => {
  if (!result.value) return null
  if (!result.value.job && !result.value.artifacts) return null
  return buildRunSummary({
    job: result.value.job,
    artifacts: result.value.artifacts,
  })
})

const publishFailed = computed(() => runSummary.value?.status === 'FAILED')

function isStageMissingBecausePublishFailed(stage: PublishObservedStage): boolean {
  if (!publishFailed.value) return false
  const summary = stageSummaryFor(stage)
  if (summary?.availability === 'available') return false
  return true
}

const inventoryChanges = computed(() => buildInventoryChanges(selectCompleteRunMetrics({
  runMetricsCounts: result.value?.runMetricsCounts ?? null,
  job: result.value?.job ?? null,
  runMetrics: result.value?.runMetrics ?? null,
})))
const publishOutcome = computed(() =>
  buildPublishOutcome({
    runMetricsCounts: result.value?.runMetricsCounts ?? null,
    job: result.value?.job ?? null,
    runMetrics: result.value?.runMetrics ?? null,
  }),
)

/* -------------------------------------------------------------------------- */
/* Website Publish & Refresh (final stage)                                     */
/* -------------------------------------------------------------------------- */

/**
 * Website Visibility Changes subsection — re-presents the existing Run Summary
 * outcome deltas as the final-stage live-website outcome. Per-VIN reason
 * records are not captured by the publish flow, so `recordsAvailable` stays
 * false and the UI shows an honest note instead of a drilldown.
 */
const websiteVisibilityChanges = computed(() =>
  buildWebsiteVisibilityChangesViewModel({
    hasOutcome: publishOutcome.value.hasOutcome,
    activityComplete: inventoryChanges.value.hasMetrics,
    liveAfter: publishOutcome.value.liveWebsiteCount,
    added: publishOutcome.value.added,
    removed: publishOutcome.value.removed,
    updated: publishOutcome.value.updated,
    madeVisible: publishOutcome.value.madeVisible,
    hidden: publishOutcome.value.hidden,
    recordsAvailable: false,
    // Dedupe VINs across buckets so a vehicle that was both added and made
    // visible counts once in the headline (the bucket sum double-counts it).
    uniqueChangedVins: countUniqueChangedWebsiteVins({
      job: result.value?.job ?? null,
      runMetrics: result.value?.runMetrics ?? null,
    }),
  }),
)

/**
 * Cache/CDN refresh details for this run, sourced from the publish-flow
 * observability flow-metrics (`publishWide.websiteRefresh`). When the backend
 * did not capture a refresh outcome (e.g. older runs, manual-only publishes),
 * this is null and the Website Refresh subsection renders an honest
 * "not captured" state.
 */
const websiteRefreshDetails = computed<WebsiteRefreshDetails | null>(() => {
  const refresh = flowMetrics.value?.publishWide.websiteRefresh
  if (!refresh) return null
  return {
    status: refresh.status,
    mechanism: refresh.mechanism,
    startedAt: refresh.startedAt ?? null,
    finishedAt: refresh.finishedAt ?? null,
    durationMs: refresh.durationMs ?? null,
    requestId: refresh.requestId ?? null,
    executionArn: refresh.executionArn ?? null,
    jobId: refresh.jobId ?? null,
    keyCount: refresh.keyCount ?? null,
    keyTypeCounts: refresh.keyTypeCounts ?? null,
    recordsAvailable: refresh.recordsAvailable ?? null,
    keys: refresh.keys ?? null,
    pathCount: refresh.pathCount ?? null,
    paths: refresh.paths ?? null,
    message: refresh.message ?? null,
    errorMessage: refresh.errorMessage ?? null,
  }
})

const websiteRefresh = computed(() => buildWebsiteRefreshViewModel(websiteRefreshDetails.value))

// Refreshed Cache Keys dialog — BACKEND-paginated + backend-searched. The full
// key list is never sent in the metrics payload; the dialog fetches pages from
// the `website-refresh-keys` API so a user can find a VIN/key that is not yet
// loaded. State is local to the view (the dialog has a single owner).
const WEBSITE_REFRESH_KEYS_PAGE_SIZE = 50
const WEBSITE_REFRESH_KEYS_SEARCH_DEBOUNCE_MS = 250

const websiteRefreshKeysDialogOpen = ref(false)
const websiteRefreshKeySearch = ref<string | null>('')
const websiteRefreshKeyRows = ref<WebsiteRefreshKeyRecord[]>([])
const websiteRefreshKeysCursor = ref<string | undefined>(undefined)
const websiteRefreshKeysTotal = ref(0)
const websiteRefreshKeysLoading = ref(false)
const websiteRefreshKeysUnavailable = ref(false)
const websiteRefreshKeysSearchActive = ref(false)

const websiteRefreshKeysHasMore = computed(
  () =>
    typeof websiteRefreshKeysCursor.value === 'string' &&
    websiteRefreshKeysCursor.value.length > 0,
)

// Guards against out-of-order responses (a fast Load More / search swap must
// never let a stale page overwrite a newer one).
let websiteRefreshKeysRequestId = 0
let websiteRefreshKeysSearchTimer: ReturnType<typeof setTimeout> | null = null

function websiteRefreshCacheKeyTypeLabel(type: WebsiteRefreshKeyType): string {
  if (type === 'vehicle') return 'Vehicle'
  if (type === 'account') return 'Account'
  return 'Other'
}

function currentWebsiteRefreshKeysSearch(): string {
  return (websiteRefreshKeySearch.value ?? '').trim()
}

async function fetchWebsiteRefreshKeysPage(options: {
  append: boolean
  search: string
}): Promise<void> {
  const jazelId = accountStore.accountId
  const sessionToken = accountStore.sessionToken
  const cid = correlationId.value
  if (!jazelId || !sessionToken || !cid) {
    websiteRefreshKeysUnavailable.value = true
    return
  }

  const requestId = ++websiteRefreshKeysRequestId
  websiteRefreshKeysLoading.value = true
  if (!options.append) websiteRefreshKeysUnavailable.value = false

  const result = await tryGetWebsiteRefreshKeys({
    endpoint,
    jazelId,
    correlationId: cid,
    sessionToken,
    pageSize: WEBSITE_REFRESH_KEYS_PAGE_SIZE,
    ...(options.search.length > 0 ? { search: options.search } : {}),
    ...(options.append && websiteRefreshKeysCursor.value
      ? { cursor: websiteRefreshKeysCursor.value }
      : {}),
  })

  // Drop stale responses.
  if (requestId !== websiteRefreshKeysRequestId) return
  websiteRefreshKeysLoading.value = false

  if (result.status !== 'ok') {
    if (!options.append) {
      websiteRefreshKeyRows.value = []
      websiteRefreshKeysCursor.value = undefined
      websiteRefreshKeysTotal.value = 0
    }
    websiteRefreshKeysUnavailable.value = true
    return
  }

  websiteRefreshKeysUnavailable.value = false
  websiteRefreshKeysSearchActive.value = options.search.length > 0
  websiteRefreshKeyRows.value = options.append
    ? [...websiteRefreshKeyRows.value, ...result.data.records]
    : result.data.records
  websiteRefreshKeysCursor.value = result.data.nextCursor
  websiteRefreshKeysTotal.value = result.data.totalRecords
}

function loadMoreWebsiteRefreshKeys(): void {
  if (websiteRefreshKeysLoading.value || !websiteRefreshKeysHasMore.value) return
  void fetchWebsiteRefreshKeysPage({
    append: true,
    search: currentWebsiteRefreshKeysSearch(),
  })
}

function openWebsiteRefreshKeysDialog(): void {
  if (websiteRefreshKeysSearchTimer) {
    clearTimeout(websiteRefreshKeysSearchTimer)
    websiteRefreshKeysSearchTimer = null
  }
  websiteRefreshKeySearch.value = ''
  websiteRefreshKeyRows.value = []
  websiteRefreshKeysCursor.value = undefined
  websiteRefreshKeysTotal.value = 0
  websiteRefreshKeysSearchActive.value = false
  websiteRefreshKeysUnavailable.value = false
  websiteRefreshKeysDialogOpen.value = true
  void fetchWebsiteRefreshKeysPage({ append: false, search: '' })
}

watch(websiteRefreshKeySearch, (value) => {
  if (!websiteRefreshKeysDialogOpen.value) return
  if (websiteRefreshKeysSearchTimer) clearTimeout(websiteRefreshKeysSearchTimer)
  const term = (value ?? '').trim()
  websiteRefreshKeysSearchTimer = setTimeout(() => {
    void fetchWebsiteRefreshKeysPage({ append: false, search: term })
  }, WEBSITE_REFRESH_KEYS_SEARCH_DEBOUNCE_MS)
})

const websitePublishRefreshStatus = computed(() =>
  aggregateWebsitePublishRefreshStatus({
    publishSucceeded:
      runSummary.value?.status === 'SUCCEEDED' || publishOutcome.value.hasOutcome,
    publishFailed: publishFailed.value,
    refreshStatus: websiteRefresh.value.status,
  }),
)
const websitePublishRefreshSummaryLine = computed(() =>
  buildWebsitePublishRefreshSummaryLine({
    visibility: websiteVisibilityChanges.value,
    refresh: websiteRefresh.value,
  }),
)
const rawLogsJson = computed(() => {
  if (!result.value?.logs) return ''
  const raw = result.value.logs.raw
  if (typeof raw === 'string' && raw.length > 0) return raw
  try {
    return JSON.stringify(result.value.logs.entries ?? [], null, 2)
  } catch {
    return ''
  }
})

async function loadFlow() {
  if (!accountStore.accountId) {
    result.value = null
    observability.value = null
    return
  }
  if (!correlationId.value) {
    result.value = null
    observability.value = null
    return
  }
  isLoading.value = true
  rawObservabilityExpanded.value = false
  aggregatedErrorMessage.value = ''
  unavailablePublishFlowFiles.value = new Set()
  showPublishFlowFileError.value = false
  publishFlowFileError.value = ''
  try {
    result.value = await fetchPublishFlow(endpoint, accountStore.accountId, correlationId.value, {
      includeVins: true,
      maxVins: 500,
    })
  } catch (e) {
    aggregatedErrorMessage.value = e instanceof Error ? e.message : String(e)
    result.value = null
  } finally {
    isLoading.value = false
  }
  // Independent of the legacy aggregator; failures here never block the
  // page. The card itself self-gates and emits structured warns when
  // unavailable. Skip silently when prerequisites are missing.
  await loadObservability()
}

async function loadObservability() {
  if (!accountStore.accountId || !accountStore.sessionToken || !correlationId.value) {
    observability.value = null
    return
  }
  try {
    observability.value = await fetchPublishFlowObservability({
      endpoint,
      jazelId: accountStore.accountId,
      correlationId: correlationId.value,
      sessionToken: accountStore.sessionToken,
    })
  } catch (e) {
    // try* propagates programmer/auth errors; treat as observability noise.
    console.warn(
      JSON.stringify({
        event: 'publishFlow.observability.loadFailed',
        correlationId: correlationId.value,
        message: e instanceof Error ? e.message : String(e),
      }),
    )
    observability.value = null
  }
}

function goBack() {
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push('/')
  }
}

async function downloadPreProcessorSourceFile(row: PreProcessorSourceRow) {
  await downloadPublishFlowFile(preProcessorSourceFilePointer(row), 'sourceInput')
}

async function downloadPreProcessorFeedSourceFile(row: FlowFeedSourceCount) {
  await downloadPublishFlowFile(preProcessorSourceFilePointer(row), 'sourceInput')
}

async function downloadPreProcessorOutputFile() {
  await downloadPublishFlowFile(preProcessorOutputFilePointer.value, 'preProcessorOutput')
}

async function onDetailedPublishStageAction(actionId: string) {
  if (actionId.startsWith('preProcessorSource:')) {
    const idx = Number(actionId.split(':')[1])
    const src = preProcessorInputsVisibleSources.value[idx]
    if (!src) return
    await downloadPreProcessorSourceFile(src)
    return
  }

  if (actionId === 'preProcessorOutput') {
    await downloadPreProcessorOutputFile()
  }
}

function showPublishFlowFileDownloadError(message: string) {
  publishFlowFileError.value = message
  showPublishFlowFileError.value = true
}

async function downloadPublishFlowFile(
  pointer: ReturnType<typeof preProcessorSourceFilePointer>,
  fileRole: PublishFlowFileRole,
) {
  if (!pointer) return

  const jazelId = String(accountStore.accountId || '').trim()
  if (!jazelId) return

  const cacheKey = publishFlowFileCacheKey(pointer, fileRole)
  downloadingPublishFlowFileKey.value = cacheKey
  try {
    const presignedUrl = await requestPublishFlowFilePresign({
      endpoint,
      jazelId,
      correlationId: correlationId.value,
      pointer,
      fileRole,
      sessionToken: accountStore.sessionToken || undefined,
    })
    openExternalDownloadUrl(presignedUrl)
  } catch (error) {
    const message =
      error instanceof PublishFlowFilePresignError
        ? error.message
        : publishFlowFileUnavailableMessage(fileRole) + '.'
    if (
      error instanceof PublishFlowFilePresignError &&
      (error.code === 'FILE_NOT_FOUND' || error.code === 'SOURCE_FILE_NOT_AVAILABLE_FOR_RUN')
    ) {
      unavailablePublishFlowFiles.value = new Set([...unavailablePublishFlowFiles.value, cacheKey])
    }
    showPublishFlowFileDownloadError(message)
    console.error('Failed to download publish flow file', error)
  } finally {
    downloadingPublishFlowFileKey.value = ''
  }
}

function formatTriggerLabel(summary: RunSummaryViewModel): string {
  if (summary.triggerType === 'user') {
    return summary.requestedByUsername ? `User · ${summary.requestedByUsername}` : 'User'
  }
  if (summary.triggerType === 'feed') return 'Feed (automatic)'
  return 'Unknown'
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.round(ms / 100) / 10
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(ms / 60000)
  const remainderSeconds = Math.round((ms % 60000) / 1000)
  return `${minutes}m ${remainderSeconds}s`
}

onMounted(loadFlow)
watch(() => accountStore.accountId, loadFlow)
watch(correlationId, () => {
  closeAllStageDetails()
  detailedPublishImpactStore.resetTransientState()
  void loadFlow()
})
</script>

<style scoped>
.cursor-help {
  cursor: help;
}

.correlation-chip {
  font-family: 'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 12px;
  background: #f1f5f9;
  color: #334155;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  word-break: break-all;
}

.publish-outcome-hero {
  display: flex;
  align-items: center;
  gap: 16px;
}

.publish-outcome-count {
  min-width: 88px;
  font-size: 44px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: #171a2b;
}

.publish-outcome-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 12px;
}

.publish-outcome-metric {
  border: 1px solid #e3e7ef;
  border-radius: 12px;
  padding: 12px;
  background: #ffffff;
}

.publish-outcome-metric-value {
  font-size: 24px;
  line-height: 1.1;
  font-weight: 700;
  color: #171a2b;
}

.publish-outcome-metric-label {
  margin-top: 4px;
  font-size: 12px;
  color: #66708a;
}

.run-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.run-summary-cell {
  min-width: 0;
}

.run-summary-cell--wide {
  grid-column: span 2;
}

.summary-inventory-outcome-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 960px) {
  .summary-inventory-outcome-grid {
    grid-template-columns: 1fr;
  }
}

.file-key {
  word-break: break-all;
  font-family: 'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 12px;
}

.website-refresh-key-cell {
  max-width: 640px;
}

.stage-detail-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-block: 2px;
}

.stage-detail-row span:last-child {
  text-align: right;
}

.pre-processor-section-title {
  letter-spacing: 0.06em;
  font-weight: 600;
}

.pre-processor-input-name {
  min-width: 0;
  word-break: break-word;
}

.changes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.config-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.stage-card-section + .stage-card-section {
  margin-top: 4px;
}

.stage-card-section-title {
  letter-spacing: 0.08em;
  font-size: 0.6875rem;
  margin-bottom: 2px;
  opacity: 0.72;
}

.publish-flow-business-section-header {
  padding-bottom: 0;
}

.publish-stage-outcome {
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 0;
}

.publish-stage-flow {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.publish-stage-flow-row {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  column-gap: 12px;
  position: relative;
}

.publish-stage-flow-row + .publish-stage-flow-row {
  margin-top: 2px;
}

.publish-stage-flow-rail {
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 2px;
}

.publish-stage-flow-marker {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  background: #eef6ff;
  border: 1px solid rgba(var(--v-theme-primary), 0.28);
  box-shadow: 0 0 0 3px rgb(var(--v-theme-surface));
  z-index: 1;
}

.publish-stage-flow-connector {
  position: absolute;
  top: 30px;
  bottom: -8px;
  width: 1px;
  background: rgba(var(--v-theme-on-surface), 0.16);
}

.publish-stage-flow-row--last .publish-stage-flow-connector {
  display: none;
}

.publish-stage-flow-content {
  min-width: 0;
  padding-bottom: 10px;
  overflow: visible;
}

.publish-stage-flow-row + .publish-stage-flow-row .publish-stage-flow-content {
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.publish-stage-flow-row--last .publish-stage-flow-content {
  padding-bottom: 0;
}

.publish-stage-handoff {
  line-height: 1.25;
  margin-top: 4px;
  margin-bottom: 0;
}

.publish-stage-actions {
  margin-top: 8px;
  margin-bottom: 0;
}

.publish-stage-details-toggle {
  min-width: 0;
  opacity: 0.78;
  font-weight: 500;
}

.publish-stage-details-toggle:hover {
  opacity: 1;
}

.publish-stage-expanded-details {
  position: relative;
  display: block;
  margin-top: 12px;
  overflow: visible;
}

.publish-stage-details-enter-active,
.publish-stage-details-leave-active {
  transition: opacity 120ms ease;
}

.publish-stage-details-enter-from,
.publish-stage-details-leave-to {
  opacity: 0;
}

.feed-field-rules-nested-subsection--quiet {
  /* Supporting information — stays visually secondary to Vehicle Data Mapping. */
}

.publish-flow-raw-observability {
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.publish-flow-raw-observability .v-card-text {
  padding-top: 12px;
  padding-bottom: 12px;
}

.feed-field-rules-nested-subsection--noticeable {
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.feed-field-rules-nested-subsection--attention {
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.sub-stage-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.sub-stage-status-chip {
  opacity: 0.82;
  font-size: 0.6875rem;
}
</style>
